import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDataEngine } from '@dhis2/app-runtime';
import {
  Button, Input, TextArea, SingleSelect,
  SingleSelectOption, AlertBar, CircularLoader,
  Card, Modal, ModalTitle, ModalContent, ModalActions, IconSearch24, colors,
  IconArrowDown24
} from '@dhis2/ui';
import { DndProvider, useDrag } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  shouldShowObjective,
  getCurrentUserWithRole,
} from './utils/userRoleUtils';
import { useNavigate } from "react-router-dom";
import { isWithinFiscalYear } from '../GoalsManagement/utils/fiscalYearHelpers';
import { ActivityIcon, CheckCircleIcon, CheckIcon, UserRoundIcon, UsersRoundIcon } from "lucide-react";
import { GiNotebook, GiProgression } from "react-icons/gi";
import { TbHandFingerDown, TbPointerDown } from "react-icons/tb";
import { FileAttachment } from './components/FileAttachment';
import { useFiscalYear } from '../../shared/FiscalYearContext';
import './PersonalPerfomNotes.css';
import {
  MANAGER_COMMENTS_DE,
  parseManagerComments,
} from '../../features/objectivesWeeklyReporting/utils/managerComments';

// ============================================================================
// STATUS CONFIGURATION - Single source of truth for statuses
// ============================================================================
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Not started' },
  { value: 'inprogress', label: 'In progress' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'abandomed', label: 'Abandoned' },
  { value: 'completed', label: 'Completed' },
];


// Data Element Mappings for Personal Performances program stage
const TASK_DATAELEMENT_MAPPING = {
  selectedWeek: 'EiL3lPaL908',
  progressPercent: 'AElpNxzTdRh',
  status: 'LuTwfuN34pc',
  achievements: 'j9FdNkmPpXM',
  challenges: 'WSQ9nuXPnNL',
  nextSteps: 'M7PG2p2XsDA',
  objectiveId: 'plJovUlxfQ0',
  taskName: 'I0QtZkMbzxN',
  taskDescription: 'LR70eqTjB8m',
  kpiId: 'uXeH0X0aaCl',
  positionTei: 'SlgO60cMK3F',
  incumbentStaffId: 'OHKFI8GuxPs',
  subTasks: 'YBXgnhyue59',
  shareWithManager: 'gNCdwjmtsiQ',
  sealed: 'H3tI2quMhAk',
  stretchTier: 'DCPrEyuscRy',
  stretchCategories: 'fYwQVec3ziZ',
  stretchEvidence: 'ZVdO5T0fHOf',
  touched: 'zftuWGwXZce',
};

// Stretch claim options — used by the multi-select in the PPN form
const STRETCH_CATEGORY_OPTIONS = [
  { value: 'scope', label: 'Delivered more scope than targeted' },
  { value: 'quality', label: 'Delivered at higher quality than standard' },
  { value: 'time', label: 'Delivered significantly earlier' },
  { value: 'innovation', label: 'Innovated / improved the process' },
  { value: 'mentoring', label: 'Mentored / unblocked others' },
  { value: 'outside', label: 'Took on work outside PPN scope' },
];

// Stretch tier options — mutually exclusive, drive the appraisal score
const STRETCH_TIERS = {
  NONE: '',
  TARGET_EXCEEDED: 'target_exceeded',
  EXCEPTIONAL: 'exceptional',
};

const STRETCH_TIER_CONFIG = {
  [STRETCH_TIERS.TARGET_EXCEEDED]: {
    value: STRETCH_TIERS.TARGET_EXCEEDED,
    label: 'Target Exceeded',
    description: 'I exceeded 100% of the agreed target.',    
    color: '#4f46e5',
    bg: '#eef2ff',
    border: '#c7d2fe',
  },
  [STRETCH_TIERS.EXCEPTIONAL]: {
    value: STRETCH_TIERS.EXCEPTIONAL,
    label: 'Exceptional Achievement',
    description: 'Exceptional results with substantial additional value to the Company.',    
    color: '#b45309',
    bg: '#fef3c7',
    border: '#fcd34d',
  },
};

// Progress tier naming — display-only, derived from taskName + progressPercent
const getProgressTier = (taskName, progressPercent) => {
  const hasName = Boolean(taskName && taskName.trim());

  if (!hasName) {
    return {
      key: 'not_started',
      label: 'Not Started',
      caption: 'No Personal Performance Note has been created yet.',
      color: '#94a3b8',
    };
  }

  const pct = Number(progressPercent) || 0;

  if (pct < 40) {
    return {
      key: 'initial',
      label: 'Initial Progress',
      caption: 'Activities are starting, and initial steps towards the objective are underway.',
      color: '#dc3545',
    };
  }
  if (pct < 80) {
    return {
      key: 'good',
      label: 'Good Progress',
      caption: 'Implementation is underway, with measurable progress towards the agreed target.',
      color: '#ffc107',
    };
  }
  return {
    key: 'advanced',
    label: 'Advanced Progress / Completed',
    caption: 'The objective is nearing completion or has been achieved.',
    color: '#28a745',
  };
};

// Close-below-100 reasons — used when sealing at progress < 100%
const SEAL_REASON_OPTIONS = [
  { value: 'cancelled', label: '🚫 Cancelled / Abandoned' },
  { value: 'postponed', label: '⏸️ Postponed indefinitely' },
  { value: 'blocked', label: '🚧 Blocked by external factor' },
  { value: 'fy_ended', label: '🗓️ Fiscal year ended before completion' },
  { value: 'replaced', label: '🔄 Replaced by another PPN' },
];


const KPI_PROGRAM_STAGE_ID = 'ivyCLjNLFrd';
const KPI_PROGRAM_ID = 'yiT00XKrrHP';

const TASK_PROGRAM_STAGE_ID = 'Nv9nYNcBTdM';
const PROGRAM_ID = 'yiT00XKrrHP';

// Auto-save configuration
const AUTO_SAVE_DELAY = 3000; // 3 seconds
const DRAFT_NAMESPACE = 'weekly-progress-drafts';
const NEUTRAL_CHALLENGE_PHRASES = new Set([
  '',
  'na',
  'n a',
  'none',
  'none yet',
  'not applicable',
  'no challenge',
  'no challenge yet',
  'no challenge at the moment',
  'no challenges',
  'no challenges yet',
  'no challenges at the moment',
  'nothing yet',
  'no issue',
  'no issues',
  'no issue yet',
  'no issues yet',
  'no problem',
  'no problems',
  'no problem yet',
  'no problems yet'
]);

const normalizeChallenge = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ');

const hasMeaningfulChallenge = (value) => {
  const normalized = normalizeChallenge(value);
  return normalized.length >= 5 && !NEUTRAL_CHALLENGE_PHRASES.has(normalized);
};

const sortTasksDeterministically = (arr) =>
  [...arr].sort((a, b) => {
    const aKey = a.eventId || a.id || '';
    const bKey = b.eventId || b.id || '';
    return aKey < bKey ? -1 : aKey > bKey ? 1 : 0;
  });

const OBJECTIVE_DATA_ELEMENTS = {
  START_DATE: 'eDcVy661j9e',
  COMPLETION_DATE: 'J0rjxrDKn1l',
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
/* ============================================================ */
  /* REMOVE UP/DOWN SPIN BUTTONS FROM NUMBER INPUTS               */
  /* ============================================================ */
  
  /* Chrome, Safari, Edge, Opera - hide spin buttons */
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox - hide spin buttons */
  input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  /* ============================================================ */
  /* RANGE INPUT STYLES (for progress slider)                     */
  /* ============================================================ */
  
  input[type="range"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    height: 8px;
    border-radius: 4px;
    background: #e9ecef;
    outline: none;
    width: 100%;
    margin: 10px 0;
    position: relative;
  }

  /* WebKit (Chrome, Safari) */
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #6c757d;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,1);
    pointer-events: auto;
  }

  input[type="range"]:hover::-webkit-slider-thumb {
    background: #555;
  }

  /* Firefox */
  input[type="range"]::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #6c757d;
    cursor: pointer;
    border: none;
    box-shadow: 0 4px 8px rgba(0,0,0,1);
    pointer-events: auto;
  }

  input[type="range"]:hover::-moz-range-thumb {
    background: #555;
  }

  input[type="range"]::-moz-range-track {
    background: #e9ecef;
    border-radius: 4px;
    height: 8px;
  }

  /* ============================================================ */
  /* SINGLESELECT - Keep only the dropdown chevron                */
  /* ============================================================ */
  
  /* Ensure SingleSelect shows a clean dropdown indicator */
  .dhis2-singleselect .dhis2-select__indicator {
    padding: 0 8px !important;
  }
  
  .dhis2-singleselect .dhis2-select__indicator-separator {
    display: none !important;
  }
  
  /* Style the chevron to look clean */
  .dhis2-singleselect .dhis2-select__dropdown-indicator {
    color: #6c757d !important;
  }
  
  .dhis2-singleselect .dhis2-select__dropdown-indicator svg {
    width: 20px !important;
    height: 20px !important;
  }
@keyframes pulse-amber {
  0%, 100% { 
    background-color: rgba(241, 142, 13, 0.3);
    box-shadow: 0 0 0 0 rgba(241, 142, 13, 0.7);
  }
  50% { 
    background-color: rgba(241, 142, 13, 0.6);
    box-shadow: 0 0 0 4px rgba(241, 142, 13, 0);
  }
}

@keyframes pulse-green {
  0%, 100% { 
    background-color: rgba(40, 167, 69, 0.3);
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  50% { 
    background-color: rgba(40, 167, 69, 0.6);
    box-shadow: 0 0 0 4px rgba(40, 167, 69, 0);
  }
}

@keyframes pulse-blue {
  0%, 100% { 
    background-color: rgba(23, 162, 184, 0.3);
    box-shadow: 0 0 0 0 rgba(23, 162, 184, 0.7);
  }
  50% { 
    background-color: rgba(23, 162, 184, 0.6);
    box-shadow: 0 0 0 4px rgba(23, 162, 184, 0);
  }
}

    @keyframes point {
    0%, 100% {
      transform: translateX(0);
      opacity: 1;
    }
    25% {
      transform: translateX(10px);
      opacity: 0.8;
    }
    50% {
      transform: translateX(-5px);
      opacity: 1;
    }
    75% {
      transform: translateX(5px);
      opacity: 0.9;
    }
  }
  
  @keyframes bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }
  
  .finger-pointer {
    animation: point 1.5s ease-in-out infinite;
    display: inline-block;
    font-size: 2rem;
  }
  
  .kpi-card-highlight {
    transition: all 0.3s ease;
    cursor: pointer;
  }
  
  .kpi-card-highlight:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  }
  
  @keyframes pulse-ring {
    0% {
      box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(231, 76, 60, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(231, 76, 60, 0);
    }
  }
  
  .pulse-animation {
    animation: pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
  }
`;
document.head.appendChild(styleSheet);

// Draggable KPI Card Component
const DraggableKPICard = ({ kpi, isSelected, onSelect }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'kpi',
    item: { kpi },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        marginBottom: '10px',
        marginTop: '10px',
        marginLeft: '10px',
        boxShadow: "0 8px 10px rgba(0, 0, 0, 0.3)",
        borderRadius: '14px',
        backgroundColor: 'rgb(230, 237, 152)'

      }}
      onClick={() => onSelect(kpi)}
    >
      <Card
        style={{
          border: isSelected ? '2px solid #e74c3c' : '1px solid #ddd',
          backgroundColor: isSelected ? 'rgb(242, 245, 206)' : 'white',
        }}
      >
        <div style={{ padding: '12px', borderRadius: '8px' }}>
          <h5 style={{ margin: '0 0 4px 0', color: '#2c3e50' }}>
            {kpi.name}
          </h5>
          {kpi.description && (
            <p style={{ margin: '0 0 4px 0', fontSize: '0.8rem', color: '#7f8c8d' }}>
              {kpi.description}
            </p>
          )}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.7rem',
            color: '#95a5a6'
          }}>
            <span>Measure: {kpi.target || 'N/A'}</span>
          </div>
        </div>
      </Card>
    </div>
);
};
 
const CommentsPanel = ({ rawComments, loading = false }) => {
  const comments = useMemo(() => parseManagerComments(rawComments), [rawComments]);

  if (loading) {
    return (
      <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b' }}>
        <CircularLoader small /> Loading manager comments…
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '24px',
      border: '1px solid #e0e7ff',
      borderRadius: '10px',
      overflow: 'hidden',
      background: '#f5f8ff',
    }}>
      <div style={{
        padding: '12px 16px',
        background: '#e0e7ff',
        borderBottom: '1px solid #c7d2fe',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <span style={{ fontSize: '1.2rem' }}>💬</span>
        <span style={{ fontWeight: 600, color: '#3730a3' }}>Manager Comments</span>
        <span style={{
          marginLeft: 'auto',
          fontSize: '0.75rem',
          color: '#4338ca',
          background: '#fff',
          padding: '2px 10px',
          borderRadius: '12px',
          fontWeight: 600,
        }}>{comments.length}</span>
      </div>
      <div style={{ padding: '16px' }}>
        {comments.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>
            No comments from your manager yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {comments.map((c, i) => (
              <div key={i} style={{
                padding: '12px 14px',
                background: '#fff',
                borderRadius: '8px',
                borderLeft: '3px solid #4f46e5',
                boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  fontSize: '0.72rem', color: '#64748b', marginBottom: '6px',
                }}>
                  <span style={{
                    width: '24px', height: '24px', borderRadius: '50%',
                    background: '#eef2ff', color: '#4338ca',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 700,
                  }}>{(c.commentedBy || 'M')[0].toUpperCase()}</span>
                  <strong style={{ color: '#334155' }}>{c.commentedBy}</strong>
                  <span>•</span>
                  <span>{new Date(c.commentedAt).toLocaleString()}</span>
                </div>
                <div style={{ fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {c.comment}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// EMAIL NOTIFICATION UTILITIES - Personal Performance-aware duplicate prevention
// ============================================================================

// Generate unique key for task+week+content
const getChallengeEmailKey = (task, week, challenges) => {
  const taskId = task?.eventId || task?.id || 'new';
  const contentHash = simpleHash(challenges?.trim() || '');
  return `challenge_email_${taskId}_${week}_${contentHash}`;
};

// Simple hash function for content deduplication
const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36).substring(0, 8);
};

// Check if email should be sent (with cooldown logic)
const shouldSendChallengeEmail = (key, minCooldownMs = 15 * 60 * 1000) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return { shouldSend: true, reason: 'first_time' };

    const data = JSON.parse(stored);
    const now = Date.now();

    // Allow if cooldown period has passed
    if (now >= data.cooldownUntil) {
      return { shouldSend: true, reason: 'cooldown_expired' };
    }

    // Block if still in cooldown
    return {
      shouldSend: false,
      reason: 'in_cooldown',
      remainingMs: data.cooldownUntil - now,
      lastSent: data.lastSent
    };
  } catch (e) {
    console.warn('⚠️ [EMAIL] Failed to parse tracking data, allowing send:', e);
    return { shouldSend: true, reason: 'parse_error_fallback' };
  }
};

// Mark email as sent (with cooldown)
const markChallengeEmailSent = (key, cooldownMs = 15 * 60 * 1000) => {
  try {
    const data = {
      lastSent: Date.now(),
      cooldownUntil: Date.now() + cooldownMs,
      version: 1
    };
    localStorage.setItem(key, JSON.stringify(data));
    console.log('✅ [EMAIL] Marked as sent with cooldown:', { key, cooldownMs });
  } catch (e) {
    console.warn('⚠️ [EMAIL] Failed to store tracking data:', e);
  }
};

const objectiveBelongsToFiscalYear = (objective, fiscalYear) => {
  if (!fiscalYear?.start || !fiscalYear?.end) return true;

  const start = (objective.startDate || objective.eventDate || '').split('T')[0];
  const end   = (objective.completionDate || '').split('T')[0];

  if (!start && !end) return false;

  const effectiveStart = start || end;
  const effectiveEnd   = end || start;

  return effectiveEnd >= fiscalYear.start && effectiveStart <= fiscalYear.end;
};


// Draggable Objective Card Component
const DraggableObjectiveCard = ({
  objective,
  isSelected,
  onSelect,
  hasSubmittedTaskThisWeek,
  hasDraftThisWeek,
  isWeekLocked,
  currentTasks,
  selectedWeek,
}) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'objective',
    item: { objective },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const hasTouched = useMemo(() => {
    if (currentTasks && currentTasks.length > 0) {
      return currentTasks.some((task) => task.touched === true);
    }

    return hasDraftThisWeek === true;
  }, [currentTasks, hasDraftThisWeek]);

  // Determine status badge
  let statusBadge = null;
    if (hasSubmittedTaskThisWeek && isWeekLocked) {
      statusBadge = { label: 'LOCKED', icon: '🔒', bg: '#fff8e1', border: '#ffd54f', text: '#795548' };
    } else if (hasTouched) {
      statusBadge = { label: 'TOUCHED', icon: '✎', bg: '#ecf9e9', border: '#36ba15', text: '#2cbb08' };
    } else if (!isWeekLocked) {
      statusBadge = { label: 'NOT TOUCHED', icon: '⚠', bg: '#fff8e1', border: '#ffd54f', text: '#b76e00' };
   }

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'pointer',
        marginBottom: '10px',
      }}
      onClick={() => onSelect(objective)}
    >
      <div
        className="objective-select-card"
        style={{
          position: 'relative',
          textAlign: 'left',
          background: isSelected ? '#f0f7ff' : '#ffffff',
          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e4ea',
          borderRadius: '12px',
          padding: '16px 18px',
          boxShadow: isSelected
            ? '0 6px 18px rgba(25,118,210,0.18)'
            : '0 1px 3px rgba(0,0,0,0.06)',
          transition: 'transform 0.15s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          overflow: 'hidden',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 10px 24px rgba(25,118,210,0.16)';
            e.currentTarget.style.borderColor = '#1976d2';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
            e.currentTarget.style.borderColor = '#e0e4ea';
          }
        }}
      >
        {/* Left accent bar visible on hover */}
        <span
          aria-hidden
          className="objective-accent"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            background: 'linear-gradient(180deg, #1976d2, #42a5f5)',
            opacity: isSelected ? 1 : 0,
            transition: 'opacity 0.2s ease',
          }}
        />

        {/* Top row: name + status badge */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '6px',
          }}
        >
          <h4
            style={{
              margin: 0,
              color: '#1a202c',
              fontSize: '1rem',
              fontWeight: 600,
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {objective.name}
          </h4>

          {statusBadge && (
            <span
              style={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 10px',
                borderRadius: '20px',
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.3px',
                background: statusBadge.bg,
                border: `1px solid ${statusBadge.border}`,
                color: statusBadge.text,
                whiteSpace: 'nowrap',
              }}
            >
              <span>{statusBadge.icon}</span>
              <span>{statusBadge.label}</span>
            </span>
          )}
        </div>

        {/* Description */}
        <p
          style={{
            margin: '0 0 10px 0',
            color: '#64748b',
            fontSize: '0.85rem',
            lineHeight: 1.45,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {objective.description || 'No description available'}
        </p>

        {/* Footer: org unit | start date | select hint */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            paddingTop: '10px',
            borderTop: '1px dashed #eef1f5',
            fontSize: '0.75rem',
            color: '#94a3b8',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <span style={{ color: '#cbd5e1' }}>🏢</span>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '160px',
              }}
            >
              {objective.orgUnitName}
            </span>
          </span>

          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span
              style={{
                padding: '2px 8px',
                background: '#f1f5f9',
                borderRadius: '10px',
                fontSize: '0.7rem',
                color: '#64748b',
              }}
            >
              {objective.startDate
                ? new Date(objective.startDate).toLocaleDateString()
                : 'No date'}
            </span>
            <span
              className="objective-select-hint"
              style={{
                color: '#1976d2',
                fontWeight: 600,
                opacity: 0,
                transition: 'opacity 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              Select →
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const ObjectiveSelectionDisplay = ({ selectedObjective, onClear, hasSubmittedTaskThisWeek }) => {
  return (
    <div
      style={{
        padding: selectedObjective ? '12px 16px' : '10px 16px',
        border: selectedObjective ? '1px solid #a5d6a7' : '1px solid #b8d4f0',
        borderLeft: selectedObjective
          ? '4px solid #43a047'
          : '4px solid #1976d2',
        borderRadius: '8px',
        backgroundColor: selectedObjective ? '#e8f5e9' : '#eef4fb',
        marginBottom: '14px',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
      }}
    >
      {selectedObjective ? (
        <div style={{ textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#27ae60', fontSize: '1.0rem' }}>
            <CheckIcon size={16} /> Objective Selected
          </h4>
          <p style={{
            margin: '0 0 16px 0',
            fontWeight: 'bold',
            fontSize: '1.15rem',
            color: '#2c3e50'
          }}>
            {selectedObjective.name}
          </p>
          {hasSubmittedTaskThisWeek && (
            <div style={{
              padding: '10px',
              backgroundColor: '#fff8e1',
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid #ffc107',
              fontWeight: 500
            }}>
              📌 You already have Personal Performances for this week
            </div>
          )}
          <Button
            small
            destructive
            onClick={onClear}
            style={{ minWidth: '160px' }}
          >
            Change Objective
          </Button>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#0d47a1',
            fontSize: '0.85rem',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>🎯</span>
          <strong style={{ color: '#0d47a1' }}>Select an objective</strong>
          <span style={{ color: '#64748b' }}>to begin tracking weekly progress</span>
        </div>
      )}
    </div>
  );
};


// Helper: Get color based on progress percentage
const getProgressColor = (progress) => {
  const pct = Number(progress) || 0;
  if (pct >= 80) return '#28a745';   
  if (pct >= 40) return '#ffc107';   
  return '#dc3545';                  
};

const canAddNewTask = (weekDate) => {
  const now = new Date();
  const targetWeek = new Date(weekDate);
  const lockDate = new Date(targetWeek);
  lockDate.setDate(lockDate.getDate() + 7); // 7-day grace period
  return now <= lockDate;
};

// Check if week is in "view-only" mode (beyond 7 days)
const isViewOnlyWeek = (weekDate) => {
  return !canAddNewTask(weekDate);
};

// Add this helper function at the top of the WeeklyProgressTracker component
const isPastWeek = (weekDate) => {
  if (!weekDate) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetWeek = new Date(weekDate);
  const weekEndDate = new Date(targetWeek);
  weekEndDate.setDate(weekEndDate.getDate() + 1);
  return weekEndDate < today;
};


// ── Task switcher for multiple PPNs under the same KPI+week ──
const TaskSwitcher = ({
  tasks,
  selectedTaskIndex,
  onSelect,
  onAdd,
  onRemove,
  disabled,
}) => {
  return (
    <div
      style={{
        marginBottom: '16px',
        padding: '12px 14px',
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
        }}
      >
        <span
          style={{
            fontSize: '0.78rem',
            color: '#475569',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
          }}
        >
          Personal Performance Notes under this KPI ({tasks.length})
        </span>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={onAdd}
            disabled={disabled}
            style={{
              padding: '5px 12px',
              borderRadius: '6px',
              border: '1px solid #1976d2',
              background: '#1976d2',
              color: '#fff',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            + Add another PPN
          </button>
          {tasks.length > 1 && (
            <button
              onClick={() => onRemove(selectedTaskIndex)}
              disabled={disabled}
              style={{
                padding: '5px 12px',
                borderRadius: '6px',
                border: '1px solid #f8b4b4',
                background: '#fff',
                color: '#c53030',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
              }}
            >
              Remove current
            </button>
          )}
        </div>
      </div>

      {/* Chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {tasks.map((t, idx) => {
          const isSelected = idx === selectedTaskIndex;
          const label = t.taskName?.trim() || `Untitled PPN #${idx + 1}`;
          const progress = t.progressPercent ?? 0;
          const progressColor =
            progress >= 67 ? '#28a745' : progress >= 34 ? '#ffc107' : '#dc3545';
          return (
            <button
              key={t.id || idx}
              onClick={() => onSelect(idx)}
              style={{
                padding: '6px 12px',
                borderRadius: '18px',
                border: `1.5px solid ${isSelected ? '#1976d2' : '#cbd5e1'}`,
                background: isSelected ? '#1976d2' : '#ffffff',
                color: isSelected ? '#fff' : '#334155',
                fontSize: '0.8rem',
                fontWeight: isSelected ? 700 : 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                maxWidth: '280px',
              }}
              title={label}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '200px',
                }}
              >
                {label}
              </span>
              <span
                style={{
                  fontSize: '0.7rem',
                  padding: '1px 6px',
                  borderRadius: '10px',
                  background: isSelected ? 'rgba(255,255,255,0.25)' : progressColor + '20',
                  color: isSelected ? '#fff' : progressColor,
                  fontWeight: 700,
                }}
              >
                {progress}%
              </span>
              {!t.sealed && t.taskName && (t.progressPercent ?? 0) >= 100 && (
                <span
                  title="Complete but not closed — will not count towards appraisal"
                  style={{ fontSize: '0.75rem', color: isSelected ? '#fff' : '#c62828' }}
                >
                  ⚠️
                </span>
              )}
              {t.stretchClaimed && (
                <span
                  title={
                    t.sealed
                      ? 'Stretch claim — reviewed by manager at appraisal'
                      : 'Stretch claim — will not count unless closed'
                  }
                  style={{
                    fontSize: '0.75rem',
                    color: isSelected ? '#fff' : (t.sealed ? '#4f46e5' : '#94a3b8'),
                  }}
                >
                  📈
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};


// Replace your SubTask component with this fixed version
const SubTask = React.memo(({
  id,
  description,
  progressPercent,
  completed,
  index,
  onUpdate,
  onRemove,
  isFormDisabled,
  selectedWeek,
  showNotification
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [localDescription, setLocalDescription] = useState(description);
  const debounceTimerRef = useRef(null);
  const isLocked = completed || isFormDisabled;


  // Update local state when prop changes (for when loading existing Personal Performance)
  useEffect(() => {
    setLocalDescription(description);
  }, [description]);

  const handleDescriptionChange = useCallback(({ value }) => {
    if (!isLocked) {
      // Update local state immediately for responsive UI
      setLocalDescription(value);

      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        onUpdate(index, { description: value });
      }, 500);
    }
  }, [isLocked, onUpdate, index]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);


  const handleProgressChange = useCallback((e) => {
    if (isLocked) return;
    const newProgress = Number(e.target.value);
    if (newProgress === 100) {
      setShowConfirmDialog(true);
    } else {
      onUpdate(index, { progressPercent: newProgress, completed: false });
    }
  }, [isLocked, onUpdate, index]);

  const confirmCompletion = useCallback(() => {
    const isPastWeekCompletion = isPastWeek(selectedWeek);

    if (isPastWeekCompletion) {
      if (window.confirm('You are completing a Personal Performance Note from a past week. This will lock the Personal Performance Note form Continue?')) {
        onUpdate(index, { progressPercent: 100, completed: true });
        setShowConfirmDialog(false);
        if (showNotification) {
          showNotification('✓ Personal Performance form marked as complete for past week', 'success');
        }
      } else {
        setShowConfirmDialog(false);
      }
    } else {
      onUpdate(index, { progressPercent: 100, completed: true });
      setShowConfirmDialog(false);
    }
  }, [index, onUpdate, selectedWeek, showNotification]);

  const cancelCompletion = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [onRemove, index]);

  const renderFormField = (label, children, required = true) => (
    <div style={{ marginBottom: '5px' }}>
      <div style={{ fontWeight: '400', marginBottom: '4px' }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      {children}
    </div>
  );

  return (
    <>
      <div style={{
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        marginBottom: '8px',
        backgroundColor: completed ? '#e8f5e9' : '#f9f9f9',
        opacity: isLocked ? 0.9 : 1,
        position: 'relative'
      }}>
        {completed && (
          <div style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            backgroundColor: '#28a745',
            color: 'white',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            🔒
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            {renderFormField('Activity Description', (
              <TextArea
                value={localDescription}
                onChange={handleDescriptionChange}
                placeholder="Describe this activity"
                rows={2}
                disabled={isLocked}
                required
              />
            ))}
          </div>
          {!completed && (
            <Button
              small
              destructive
              onClick={handleRemove}
              disabled={isFormDisabled}
              style={{
                marginLeft: window.innerWidth < 768 ? '0' : '8px',
                marginTop: window.innerWidth < 768 ? '8px' : '0',
                width: window.innerWidth < 768 ? '100%' : 'auto'
              }}
            >
              Remove
            </Button>
          )}
        </div>
        <div style={{ marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: getProgressColor(progressPercent),
              minWidth: '100px'
            }}>
              Progress: {progressPercent}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={progressPercent}
              onChange={handleProgressChange}
              disabled={isLocked}
              style={{
                flex: 1,
                height: '12px',
                borderRadius: '6px',
                background: `linear-gradient(to right, ${getProgressColor(progressPercent)} 0%, ${getProgressColor(progressPercent)} ${progressPercent}%, #e9ecef ${progressPercent}%, #e9ecef 100%)`,
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                outline: 'none'
              }}
            />
          </div>
        </div>
      </div>

      {showConfirmDialog && (
        <Modal small onClose={cancelCompletion}>
          <ModalTitle>Confirm Subtask Completion</ModalTitle>
          <ModalContent>
            <p>Are you sure this subtask is completed? This action cannot be undone.</p>
          </ModalContent>
          <ModalActions>
            <button onClick={cancelCompletion}
              style={{
                backgroundColor: '#ff9800',
                borderRadius: '6px',
                marginRight: '10px',
                border: 'none',
                color: 'white'
              }}>
              Cancel
            </button>
            <Button primary onClick={confirmCompletion}>Confirm Completion</Button>
          </ModalActions>
        </Modal>
      )}
    </>
  );
});

const WeekChipSelector = ({ weeks, selectedWeek, onWeekSelect, existingTasks }) => {
  const chipRowRef = useRef(null);
  const isPastWeek = (weekDate) => {
    if (!weekDate) return false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetWeek = new Date(weekDate);
    const weekEndDate = new Date(targetWeek);
    weekEndDate.setDate(weekEndDate.getDate() + 1);
    return weekEndDate < today;
  };

  const sortedWeeks = [...weeks].sort((a, b) => new Date(b) - new Date(a));
  const visibleWeeks = sortedWeeks;

  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
        Select Week to View/Edit
      </label>
      <div style={{
          display: 'flex',
          flexWrap: 'nowrap',                 // ← single row only
          gap: '10px',
          overflowX: 'auto',                  // ← horizontal scroll
          overflowY: 'hidden',
          padding: '8px',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9',
          WebkitOverflowScrolling: 'touch',   // iOS momentum scrolling
          scrollbarWidth: 'thin',             // Firefox
          minWidth: 0,
          maxWidth: '100%',
        }}>
        {visibleWeeks.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#28a745', width: '100%' }}>
            ✓ All weeks are complete! Great job!
          </div>
        ) : (
          visibleWeeks.map((week) => {
            const isPast = isPastWeek(week);
            const tasksForWeek = existingTasks.filter(t => t.selectedWeek === week);
            const hasIncompleteTasks = tasksForWeek.some(t => t.progressPercent < 100);
            const isSelected = selectedWeek === week;

            let statusColor = '';
            let statusMessage = '';

            if (isPast && hasIncompleteTasks) {
              statusColor = '#ff9800';
              statusMessage = '⚠️ Incomplete';
            } else if (isPast && !hasIncompleteTasks) {
              // This case should not happen since we filtered out completed weeks
              statusColor = '#9e9e9e';
              statusMessage = 'Complete';
            } else {
              statusColor = '#4caf50';
              statusMessage = 'Current Week';
            }

            return (
              <div
                key={week}
                onClick={(e) => {
                  onWeekSelect(week);
                  const chipEl = e.currentTarget;
                  const rowEl = chipRowRef.current;
                  if (!rowEl) return;
                  requestAnimationFrame(() => {
                    const chipRect = chipEl.getBoundingClientRect();
                    const rowRect = rowEl.getBoundingClientRect();
                    const isFullyVisible =
                      chipRect.left >= rowRect.left && chipRect.right <= rowRect.right;
                    if (!isFullyVisible) {
                      chipEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                    }
                  });
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  backgroundColor: isSelected ? statusColor : '#ffffff',
                  color: isSelected ? '#ffffff' : '#333333',
                  border: `2px solid ${statusColor}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '12px',
                  fontWeight: isSelected ? 'bold' : 'normal',
                  boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = statusColor;
                    e.currentTarget.style.color = '#ffffff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.color = '#333333';
                  }
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', opacity: 0.8 }}>Week ending</span>
                  <span style={{ fontWeight: 'bold' }}>{week}</span>
                  <span style={{ fontSize: '9px', marginTop: '2px' }}>{statusMessage}</span>
                  {(() => {
                    const unsealedCount = existingTasks.filter(
                      t => t.selectedWeek === week && !t.sealed && t.taskName && (t.progressPercent ?? 0) >= 100
                    ).length;
                    const sealedStretchCount = existingTasks.filter(
                      t => t.selectedWeek === week && t.sealed && t.stretchClaimed
                    ).length;
                    if (unsealedCount === 0 && sealedStretchCount === 0) return null;
                    return (
                      <>
                        {unsealedCount > 0 && (
                          <span style={{ fontSize: '9px', marginTop: '2px', color: isSelected ? '#fff' : '#c62828', fontWeight: 700 }}>
                            {unsealedCount} Opened
                          </span>
                        )}
                        {sealedStretchCount > 0 && (
                          <span style={{ fontSize: '9px', marginTop: '2px', color: isSelected ? '#fff' : '#4f46e5', fontWeight: 700 }}>
                            {sealedStretchCount} Stretched
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};


function PersonalPerformanceNotes({ onSubmit, onCancel, isManagerView = false, selectedTeamMember = null }) {
  const engine = useDataEngine();
  const [currentUser, setCurrentUser] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [objectives, setObjectives] = useState([]);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [existingTasks, setExistingTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSubmittedTaskThisWeek, setHasSubmittedTaskThisWeek] = useState(false);
  const [objectiveTasks, setObjectiveTasks] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [objectiveDrafts, setObjectiveDrafts] = useState({});
  const [kpis, setKpis] = useState([]);
  const { fiscalYear, loading: fiscalYearLoading } = useFiscalYear();
  const [currentUserPosition, setCurrentUserPosition] = useState(null);

  const [kpiEnforcementActive, setKpiEnforcementActive] = useState(false);

  const challengeNotificationSentRef = useRef(false);
  const emailInProgressRef = useRef(false);
  const challengeBaselineByTaskRef = useRef({});
  const challengeNotifiedByTaskRef = useRef({});

  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]); // NEW: For manager view
  const [selectedTeamMemberTEI, setSelectedTeamMemberTEI] = useState(null); // NEW

  const [allWeeks, setAllWeeks] = useState([]);

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const autoSaveTimeoutRef = useRef(null);
  const notificationRef = useRef(null);
  const [showTaskForm, setShowTaskForm] = useState(false);

  const [sealReasonModalOpen, setSealReasonModalOpen] = useState(false);
  const [sealReasonChoice, setSealReasonChoice] = useState(null);

  const [currentStep, setCurrentStep] = useState('SELECT_OBJECTIVE'); // 'SELECT_OBJECTIVE' | 'SELECT_KPI' | 'FILL_TASK'
  const [selectedKPI, setSelectedKPI] = useState(null);

  // Multiple tasks state
  const [tasks, setTasks] = useState([{
    id: Date.now().toString(),
    taskName: "",
    taskDescription: "",
    progressPercent: 0,
    status: "pending",
    achievements: "",
    challenges: "",
    nextSteps: "",
    kpiId: "",
    kpiName: "",
    isNew: true,
    eventId: "",
  }]);

  const [selectedTaskIndex, setSelectedTaskIndex] = useState(0);
  const currentTask = tasks[selectedTaskIndex];
  const currentUserOrgUnitId = currentUser?.organisationUnits?.[0]?.id;
  const [selectedWeek, setWeekEnding] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const daysUntilSunday = (7 - day) % 7;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysUntilSunday);
    return sunday.toISOString().substring(0, 10);
  });

  const tasksRef = useRef(tasks);
  const selectedObjectiveRef = useRef(selectedObjective);
  const selectedWeekRef = useRef(selectedWeek);
  const isFormDisabledRef = useRef(false);
  const lastSeedKeyRef = useRef('');

  useEffect(() => { tasksRef.current = tasks; }, [tasks]);
  useEffect(() => { selectedObjectiveRef.current = selectedObjective; }, [selectedObjective]);
  useEffect(() => { selectedWeekRef.current = selectedWeek; }, [selectedWeek]);


  // Keep old codes in the set so historical PPNs loaded from DHIS2 still map cleanly.
  // Only the codes present in STATUS_OPTIONS are shown in the dropdown.
  const VALID_STATUS_CODES = new Set([
    'pending', 'planning', 'draft', 'inprogress',
    'postponed', 'abandomed', 'completed'
  ]);

  // Map legacy codes to their modern equivalents so old PPNs render sensibly.
  const LEGACY_STATUS_MAP = {
    planning: 'inprogress',
    draft: 'inprogress',
  };

  const mapBackendToFrontendStatus = (backendStatus) => {
    if (!backendStatus) return 'pending';
    const normalized = String(backendStatus).trim().toLowerCase();

    // Legacy codes (planning, draft) become inprogress in the UI
    if (LEGACY_STATUS_MAP[normalized]) return LEGACY_STATUS_MAP[normalized];

    if (VALID_STATUS_CODES.has(backendStatus)) return backendStatus;
    const match = [...VALID_STATUS_CODES].find(
      (code) => code.toLowerCase() === normalized
    );
    return match || 'pending';
  };

  const extractAttributes = useCallback((attributes = []) => {
    const map = {};
    attributes.forEach((attr) => {
      map[attr.attribute] = attr.value;
    });
    return map;
  }, []);

  const mapFrontendToBackendStatus = (frontendStatus) => {
    if (!frontendStatus) return 'pending';
    if (VALID_STATUS_CODES.has(frontendStatus)) return frontendStatus;
    return 'pending';
  };

  const userHasSupervisor = useMemo(() => {
    if (!currentUserPosition?.attributes) return false;
    const attr = currentUserPosition.attributes.find((a) => a.attribute === 'SjP47qi3JeB');
    return !!(attr && attr.value && String(attr.value).trim());
  }, [currentUserPosition]);

  const handleRemoveCurrentTask = async (idx) => {
    if (tasks.length <= 1) {
      showNotification('You need at least one PPN under this KPI', 'warning');
      return;
    }

    const t = tasks[idx];
    if (!t) return;

    const label = t.taskName?.trim() || 'this PPN';
    if (!window.confirm(`Delete "${label}" permanently? This cannot be undone.`)) {
      return;
    }

    // If it has a real event ID, delete from DHIS2 first
    if (t.eventId) {
      try {
        await engine.mutate({
          resource: `events/${t.eventId}`,
          type: 'delete',
        });
        console.log(`✅ Deleted event ${t.eventId}`);
      } catch (err) {
        console.error('❌ Failed to delete event:', err);
        showNotification('Failed to delete PPN from the database', 'error');
        return; // don't remove from local state if the DB delete failed
      }
    }

    // Remove from local state
    setTasks((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // Clamp selectedTaskIndex into the new range
      const newIdx = Math.max(0, Math.min(selectedTaskIndex, next.length - 1));
      setSelectedTaskIndex(newIdx);
      return next;
    });

    // Remove from existingTasks too, so it disappears from the week chip totals
    if (t.eventId) {
      setExistingTasks((prev) => prev.filter((x) => x.eventId !== t.eventId));
    }

    // Remove draft if it referenced this task
    setHasUnsavedChanges(false);
    showNotification('PPN removed', 'success');
  };

  /*
   const deleteTaskEvent = async (eventId) => {
     if (!eventId) return;
     try {
       await engine.mutate({
         resource: `events/${eventId}`,
         type: 'delete',
       });
       console.log(`✅ Deleted event ${eventId}`);
     } catch (err) {
       console.error('Failed to delete event:', err);
       showNotification('Failed to delete PPN from database', 'error');
     }
   };
   */


  const filteredObjectives = objectives.filter(obj =>
obj.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obj.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obj.orgUnitName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ============================================================================
  // TOUCHED STATUS MANAGEMENT - CONSOLIDATED
  // ============================================================================
  // TOUCHED STATUS MANAGEMENT - CONSOLIDATED
  // ============================================================================
  useEffect(() => {
    if (!selectedObjective) return;

    // Check current tasks for content
    const hasContent = tasks.some(task => {
      const hasValidTaskName = task.taskName?.trim() &&
        task.taskName !== 'Unnamed Personal Performance' &&
        task.taskName !== '';
      const hasTaskDescription = task.taskDescription?.trim()?.length > 3;
      const hasAchievements = task.achievements?.trim()?.length > 5;
      const hasChallenges = hasMeaningfulChallenge(task.challenges);
      const hasActivities = task.subTasks?.length > 0;
      const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;

      return hasValidTaskName || hasTaskDescription || hasAchievements ||
        hasChallenges || hasActivities || hasProgress;
    });

    setObjectiveDrafts(prev => {
      const currentStatus = prev[selectedObjective.id] || false;
      if (currentStatus !== hasContent) {
        return { ...prev, [selectedObjective.id]: hasContent };
      }
      return prev;
    });
  }, [tasks, selectedObjective]);


  const fetchStatusOptions = async () => {
    try {
      const response = await engine.query({
        optionSet: {
          resource: 'optionSets/U8X3RbnJrYs',
          params: { fields: 'options[code,name]' }
        }
      });

      console.log('📊 Fetched status options from U8X3RbnJrYs:', response.optionSet.options);

      if (!response.optionSet.options || response.optionSet.options.length === 0) {
        console.warn('⚠️ No options found in U8X3RbnJrYs, using fallback');
        // Use our defined STATUS_OPTIONS as fallback (already sorted)
        setStatusOptions(STATUS_OPTIONS);
        return STATUS_OPTIONS;
      }

      // Map the options from the option set to our format
      const formattedOptions = response.optionSet.options.map(opt => {
        // Find the progress percentage for this status
        const statusConfig = STATUS_OPTIONS.find(s => s.value === opt.code);
        return {
          value: opt.code,
          label: opt.name || opt.code,
          progress: statusConfig ? statusConfig.progress : 0
        };
      });

      // Sort the options by progress (lowest to highest)
      const sortedOptions = [...formattedOptions].sort((a, b) => a.progress - b.progress);

      console.log('✅ Final status options (sorted):', sortedOptions);
      setStatusOptions(sortedOptions);
      return sortedOptions;

    } catch (error) {
      console.error('❌ Error fetching status options:', error);
      // Use our defined STATUS_OPTIONS as fallback (already sorted)
      setStatusOptions(STATUS_OPTIONS);
      return STATUS_OPTIONS;
    }
  };

  const handleBackToObjective = () => {
    // Reset all task-related state
    setCurrentStep('SELECT_OBJECTIVE');
    setSelectedKPI(null);
    setTasks([{
      id: Date.now().toString(),
      taskName: "",
      taskDescription: "",
      progressPercent: 0,
      status: "pending",
      achievements: "",
      challenges: "",
      nextSteps: "",
      kpiId: "",
      kpiName: "",
      isNew: true,
      subTasks: []
    }]);
    setSelectedTaskIndex(0);
    setHasUnsavedChanges(false);
    setLastAutoSaved(null);
    setKpiEnforcementActive(false);

    // Reset week selection to current week
    const today = new Date();
    const day = today.getDay();
    const daysUntilSunday = (7 - day) % 7;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysUntilSunday);
    setWeekEnding(sunday.toISOString().substring(0, 10));

    setSelectedObjective(null);
    setHasSubmittedTaskThisWeek(false);
  };


   const getAllWeeks = useCallback(() => {
      const weeks = new Set();

      // If an objective is selected, show all weeks that objective has PPNs in —
      // regardless of which KPI each PPN belongs to.
      // If no objective is selected yet, fall back to all PPNs for this position.
      const scopedTasks = selectedObjective
        ? existingTasks.filter(t => t.objectiveId === selectedObjective.id)
        : existingTasks;

      scopedTasks.forEach(task => {
        if (task.selectedWeek) weeks.add(task.selectedWeek);
      });

      // Current week is always available as an editing anchor.
      if (selectedWeek) weeks.add(selectedWeek);

      return Array.from(weeks).sort((a, b) => new Date(b) - new Date(a));
    }, [existingTasks, selectedObjective, selectedWeek]);

  // Update allWeeks when existing tasks change
  useEffect(() => {
    const weeks = getAllWeeks();
    setAllWeeks(weeks);
  }, [getAllWeeks]);

  // Auto-show form when NO existing tasks exist (new week)
  useEffect(() => {
    if (!hasSubmittedTaskThisWeek) {
      setShowTaskForm(true);
    }
  }, [hasSubmittedTaskThisWeek]);

  // Function to check if a specific week is locked
  const isSpecificWeekLocked = (weekDate) => {
    const now = new Date();
    const targetWeek = new Date(weekDate);

    // Add 7 days grace period
    const lockDate = new Date(targetWeek);
    lockDate.setDate(lockDate.getDate() + 7);

    return now > lockDate;
  };

  const checkObjectiveHasDraft = async (objectiveId, selectedWeek) => {
    try {
      const draftId = getDraftId(objectiveId, selectedWeek);
      const response = await engine.query({
        draft: {
          resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        }
      });
      return !!response.draft;
    } catch (error) {
      return false;
    }
  };


  const fetchKPIsForObjective = async (objectiveId) => {
    try {
      // Fetch KPIs that are linked to the same objective
      const response = await engine.query({
        kpis: {
          resource: 'events',
          params: {
            program: KPI_PROGRAM_ID,
            programStage: KPI_PROGRAM_STAGE_ID,
            filter: [`plJovUlxfQ0:eq:${objectiveId}`],
            fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value]',
            pageSize: 1000
          }
        }
      });

      console.log('Raw KPI response:', response);

      // Process KPI data
      const processedKPIs = response.kpis?.events?.map(event => {
        const dataValues = event.dataValues || [];

        return {
          id: event.event, // Use event ID as KPI identifier
          eventId: event.event,
          name: dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 'Unnamed KPI',
          description: dataValues.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || '',
          target: dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value || 'N/A',
          objectiveId: dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '',
          orgUnitName: event.orgUnitName,
          eventDate: event.eventDate
        };
      }) || [];

      console.log('Processed KPIs:', processedKPIs);
      setKpis(processedKPIs);

      return processedKPIs;
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      showNotification('Failed to load KPIs for this objective', 'error');
      setKpis([]);
      return [];
    }
  };


  const handleKPISelect = async (kpi) => {
    lastSeedKeyRef.current = '';
    setSelectedKPI(kpi);
    setKpiEnforcementActive(false);
    setCurrentStep('FILL_TASK');

    let positionTeiToFilter = null;
    if (isManagerView && selectedTeamMemberTEI) {
      positionTeiToFilter = selectedTeamMemberTEI;
    } else if (currentUserPosition?.trackedEntityInstance) {
      positionTeiToFilter = currentUserPosition.trackedEntityInstance;
    }

    const exists = await checkExistingTaskThisWeek(kpi.eventId, positionTeiToFilter);
    setHasSubmittedTaskThisWeek(exists);

    // Load the PPNs for this KPI + current week
    await loadExistingTasksForWeek(selectedWeek);
  };

  const handleKPIChange = (kpi) => {
    if (kpi) {
      handleKPISelect(kpi);
    } else {
      updateCurrentTask({
        kpiId: "",
        kpiName: ""
      });
    };
  };


  // Enhanced notification system
  const showNotification = (message, type = 'error') => {
    if (window.notificationTimeout) {
      clearTimeout(window.notificationTimeout);
    }

    notificationRef.current = { message, type, timestamp: Date.now() };
    setNotification({ message, type });

    window.notificationTimeout = setTimeout(() => {
      if (notificationRef.current?.message === message) {
        setNotification(null);
        notificationRef.current = null;
      }
      window.notificationTimeout = null;
    }, 5000);
  };


  // ============================================================================
  // SEND CHALLENGE NOTIFICATION TO REPORTING MANAGER VIA NODE.JS BACKEND
  // ============================================================================
  const sendChallengeNotification = async (task, objective, challenges) => {
    // ✅ Get API base URL from environment
    const API_BASE_URL = process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'production'
        ? '/backend-api'  // ✅ Relative path (uses same protocol as page)
        : 'http://localhost:3001');


    // PPN duplicate check with content hash
    const emailKey = getChallengeEmailKey(task, selectedWeek, challenges);
    const emailCheck = shouldSendChallengeEmail(emailKey);

    if (!emailCheck.shouldSend) {
      const remainingMin = Math.ceil(emailCheck.remainingMs / 60000);
      console.log('🔄 [EMAIL] Cooldown active, skipping', {
        reason: emailCheck.reason,
        remainingMinutes: remainingMin,
        lastSent: emailCheck.lastSent ? new Date(emailCheck.lastSent).toLocaleTimeString() : null
      });
      return {
        success: true,
        message: `Notification sent recently, retry in ${remainingMin}m`,
        duplicate: true,
        cooldownRemaining: emailCheck.remainingMs
      };
    }

    console.log('🔍 [CHALLENGE EMAIL] Starting challenge notification', {
      taskName: task?.taskName,
      week: selectedWeek,
      challengesLength: challenges?.length,
      emailKey,
      checkReason: emailCheck.reason
    });

    try {
      let recipient = null;
      let recipientSource = 'unknown';
      const currentUserName = currentUser?.displayName || currentUser?.username || 'Unknown User';

      // ====== STRATEGY: Get manager from current user's position ======
      if (currentUserPosition?.attributes) {
        const attributes = {};
        currentUserPosition.attributes?.forEach(attr => {
          attributes[attr.attribute] = attr.value;
        });

        const reportingToTei = attributes['SjP47qi3JeB'];

        if (reportingToTei) {
          console.log('🔍 [CHALLENGE EMAIL] Found reportingTo TEI:', reportingToTei);
          recipientSource = 'reporting_to_tei';

          try {
            const positionResponse = await engine.query({
              reportingPosition: {
                resource: `trackedEntityInstances/${reportingToTei}`,
                params: { fields: 'trackedEntityInstance,attributes[attribute,value]' }
              }
            });

            if (positionResponse.reportingPosition) {
              const positionAttrs = {};
              positionResponse.reportingPosition.attributes?.forEach(attr => {
                positionAttrs[attr.attribute] = attr.value;
              });

              const incumbentId = positionAttrs['V66IFsw5kq6'];

              if (incumbentId) {
                try {
                  const userResponse = await engine.query({
                    managerUser: {
                      resource: `users/${incumbentId}`,
                      params: { fields: 'id,displayName,email,userCredentials[username]' }
                    }
                  });
                  if (userResponse.managerUser) {
                    recipient = userResponse.managerUser;
                    recipientSource = 'user_uid';
                    console.log('✅ [EMAIL] Found manager via User UID:', recipient.displayName);
                  }
                } catch (uidError) {
                  console.warn('⚠️ [EMAIL] User UID lookup failed:', uidError);

                  try {
                    const staffResponse = await engine.query({
                      staff: {
                        resource: `trackedEntityInstances/${incumbentId}`,
                        params: { fields: 'trackedEntityInstance,attributes[attribute,value]' }
                      }
                    });
                    const staffAttrs = {};
                    staffResponse.staff?.attributes?.forEach(attr => {
                      staffAttrs[attr.attribute] = attr.value;
                    });
                    const staffUsername = staffAttrs['g3RRIfDiQra'];
                    if (staffUsername) {
                      const userResponse = await engine.query({
                        users: {
                          resource: 'users',
                          params: {
                            filter: `userCredentials.username:eq:${staffUsername}`,
                            fields: 'id,displayName,email,userCredentials[username]',
                            pageSize: 1
                          }
                        }
                      });
                      recipient = userResponse.users?.users?.[0];
                      if (recipient) {
                        recipientSource = 'staff_tei';
                        console.log('✅ [EMAIL] Found manager via Staff TEI:', recipient.displayName);
                      }
                    }
                  } catch (staffError) {
                    console.warn('⚠️ [EMAIL] Staff TEI lookup failed:', staffError);
                  }
                }
              } else {
                console.log('🔍 [EMAIL] No incumbent found in reporting position');
              }
            }
          } catch (hierarchyError) {
            console.warn('⚠️ [EMAIL] Position hierarchy lookup failed:', hierarchyError);
          }
        } else {
          console.log('🔍 [EMAIL] No reportingTo TEI found in current user position');
        }
      } else {
        console.log('⚠️ [EMAIL] No currentUserPosition available');
      }

      // ====== STRATEGY 2: Try to get manager from position hierarchy ======
      if (!recipient && currentUserPosition?.trackedEntityInstance) {
        console.log('🔍 [EMAIL] STRATEGY 2: Trying position hierarchy');
        recipientSource = 'position_hierarchy';

        try {
          const positionResponse = await engine.query({
            position: {
              resource: `trackedEntityInstances/${currentUserPosition.trackedEntityInstance}`,
              params: {
                fields: 'trackedEntityInstance,attributes[attribute,value]'
              }
            }
          });

          if (positionResponse.position) {
            const positionAttrs = extractAttributes(positionResponse.position.attributes || []);
            const reportingToTei = positionAttrs['SjP47qi3JeB'];

            console.log('🔍 [EMAIL] Reporting to TEI:', reportingToTei);

            if (reportingToTei) {
              try {
                const reportingPositionResponse = await engine.query({
                  reportingPosition: {
                    resource: `trackedEntityInstances/${reportingToTei}`,
                    params: {
                      fields: 'trackedEntityInstance,attributes[attribute,value]'
                    }
                  }
                });

                if (reportingPositionResponse.reportingPosition) {
                  const reportingAttrs = extractAttributes(reportingPositionResponse.reportingPosition.attributes || []);
                  const incumbentId = reportingAttrs['V66IFsw5kq6'];

                  if (incumbentId) {
                    const userResponse = await engine.query({
                      user: {
                        resource: `users/${incumbentId}`,
                        params: {
                          fields: 'id,displayName,email,userCredentials[username]'
                        }
                      }
                    });

                    if (userResponse.user) {
                      recipient = userResponse.user;
                      recipientSource = 'position_hierarchy_incumbent';
                      console.log('✅ [EMAIL] Found via position hierarchy:', recipient.displayName);
                    }
                  }
                }
              } catch (hierarchyError) {
                console.warn('⚠️ [EMAIL] Position hierarchy lookup failed:', hierarchyError);
              }
            }
          }
        } catch (positionError) {
          console.warn('⚠️ [EMAIL] Position lookup failed:', positionError);
        }
      }

      // ====== STRATEGY 3: Try to get from objective's creator ======
      if (!recipient && objective) {
        console.log('🔍 [EMAIL] STRATEGY 3: Trying objective creator');
        recipientSource = 'objective_creator';

        try {
          const objectiveResponse = await engine.query({
            objectiveDetails: {
              resource: `events/${objective.id}`,
              params: {
                fields: 'storedBy,dataValues[dataElement,value]'
              }
            }
          });

          if (objectiveResponse.objectiveDetails) {
            const storedBy = objectiveResponse.objectiveDetails.storedBy;
            if (storedBy) {
              const userResponse = await engine.query({
                users: {
                  resource: 'users',
                  params: {
                    filter: `userCredentials.username:eq:${storedBy}`,
                    fields: 'id,displayName,email,userCredentials[username]',
                    pageSize: 1
                  }
                }
              });
              const user = userResponse.users?.users?.[0];
              if (user) {
                recipient = user;
                recipientSource = 'objective_creator';
                console.log('✅ [EMAIL] Found objective creator:', recipient.displayName);
              }
            }
          }
        } catch (objectiveError) {
          console.warn('⚠️ [EMAIL] Objective creator lookup failed:', objectiveError);
        }
      }

      // ====== VALIDATION ======
      if (!recipient || !recipient.email) {
        console.warn('⚠️ [EMAIL] No recipient found with email');
        console.warn('📊 [EMAIL] Debug info:', {
          hasRecipient: !!recipient,
          hasEmail: recipient?.email,
          recipientSource,
          reportingToTei: currentUserPosition?.attributes?.find(a => a.attribute === 'SjP47qi3JeB')?.value,
          userName: currentUser?.displayName,
          positionTei: currentUserPosition?.trackedEntityInstance
        });
        return {
          success: false,
          error: 'No recipient with email found',
          recipientSource,
          debugInfo: {
            hasReportingTo: !!currentUserPosition?.attributes?.find(a => a.attribute === 'SjP47qi3JeB'),
            hasPosition: !!currentUserPosition?.trackedEntityInstance,
            currentUserName
          }
        };
      }

      // ✅ Safe name resolution with fallbacks
      const recipientName = recipient.displayName ||
        recipient.name ||
        `${recipient.firstName || ''} ${recipient.surname || ''}`.trim() ||
        recipient.email?.split('@')[0] ||
        'Manager';

      console.log('✅ [EMAIL] FINAL RECIPIENT:', {
        name: recipientName,
        email: recipient.email,
        id: recipient.id,
        source: recipientSource
      });

      // ====== BUILD EMAIL CONTENT ======
      const statusText = task?.status || 'In Progress';
      const progressText = task?.progressPercent || 0;
      const taskName = task?.taskName || 'Unnamed Personal Performance';
      const objectiveName = objective?.name || 'N/A';
      const achievements = task?.achievements || 'N/A';
      const nextSteps = task?.nextSteps || 'N/A';
      const challengesText = challenges || 'No specific challenges detailed';

      const emailSubject = `⚠️ Personal Performance Challenge: ${taskName}`;
      const emailMessage = `
Dear ${recipientName},

${currentUserName} has reported challenges while working on their weekly Personal Performance.

**Personal Performance Details:**
- Personal Performance Name: ${taskName}
- Week Ending: ${selectedWeek}
- Objective: ${objectiveName}
- Progress: ${progressText}%
- Status: ${statusText}

**Challenges Reported:**
${challengesText}

**Additional Context:**
- Achievements: ${achievements}
- Next Steps: ${nextSteps}

Please see if you can provide support if needed.

Best regards,
Performance Management System
        `.trim();

      // ✅ Build HTML version - MOVE THIS INSIDE THE FUNCTION
      const buildChallengeEmailHTML = (data) => {
        const {
          recipientName: rName,
          senderName: sName,
          taskName: tName,
          selectedWeek: sWeek,
          objectiveName: oName,
          progress: prog,
          status: stat,
          challenges: chall,
          achievements: ach = 'N/A',
          nextSteps: nSteps = 'N/A'
        } = data;

        const formattedWeek = new Date(sWeek).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

        const statusColors = {
          'pending': '#ff9800',
          'in_progress': '#2196f3',
          'completed': '#4caf50',
          'approved': '#4caf50',
          'rejected': '#f44336'
        };
        const statusColor = statusColors[stat?.toLowerCase()] || '#757575';

        return [
          '<!DOCTYPE html>',
          '<html>',
          '<head>',
          '    <meta charset="UTF-8">',
          '    <title>⚠️ Personal Performance Challenge Alert</title>',
          '    <style>',
          '        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background: #f5f7fa; }',
          '        .container { background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }',
          '        .header { background: linear-gradient(135deg, #e65100, #bf360c); color: white; padding: 30px; text-align: center; }',
          '        .header-icon { font-size: 48px; margin-bottom: 8px; }',
          '        .header-title { font-size: 24px; font-weight: 700; margin: 0; }',
          '        .header-subtitle { font-size: 14px; opacity: 0.85; margin-top: 4px; }',
          '        .content { padding: 30px; }',
          '        .greeting { font-size: 20px; font-weight: 600; color: #1a202c; margin-bottom: 16px; }',
          '        .message { color: #4a5568; font-size: 16px; margin-bottom: 20px; line-height: 1.8; }',
          '        .alert-box { background: #fff4e6; border-left: 4px solid #e65100; padding: 12px 16px; border-radius: 6px; margin-bottom: 20px; }',
          '        .detail-section { background: #f8fafc; border-radius: 8px; padding: 16px; margin-bottom: 16px; border: 1px solid #edf2f7; }',
          '        .detail-section-title { font-size: 14px; font-weight: 600; color: #2d3748; margin-bottom: 12px; }',
          '        .detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }',
          '        .detail-item .label { font-size: 12px; color: #a0aec0; text-transform: uppercase; }',
          '        .detail-item .value { font-size: 15px; font-weight: 500; color: #2d3748; }',
          '        .status-badge { display: inline-block; padding: 2px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; background: ' + statusColor + '20; color: ' + statusColor + '; }',
          '        .challenge-box { background: #fff5f5; border: 1px solid #fed7d7; border-radius: 8px; padding: 16px; margin-top: 8px; }',
          '        .challenge-text { font-style: italic; color: #c53030; padding: 8px 12px; background: white; border-radius: 4px; }',
          '        .button { display: inline-block; background: #e65100; color: white; padding: 12px 28px; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 16px 0; }',
          '        .button:hover { background: #bf360c; }',
          '        .footer { text-align: center; padding: 20px; color: #718096; font-size: 13px; border-top: 1px solid #edf2f7; }',
          '        .footer-links a { color: #e65100; text-decoration: none; margin: 0 12px; }',
          '        .footer-links a:hover { text-decoration: underline; }',
          '        @media (max-width: 480px) { .detail-grid { grid-template-columns: 1fr; } }',
          '    </style>',
          '</head>',
          '<body>',
          '    <div class="container">',
          '        <div class="header">',
          '            <div class="header-icon">⚠️</div>',
          '            <div class="header-title">Personal Performance Challenge</div>',
          '            <div class="header-subtitle">' + sName + ' needs your support</div>',
          '        </div>',
          '        <div class="content">',
          '            <div class="greeting">Dear ' + rName + ',</div>',
          '            <p class="message"><strong>' + sName + '</strong> has reported challenges while working on their weekly Personal Performance. Your support and guidance would be appreciated.</p>',
          '            <div class="alert-box"><strong>🚨 Action Required:</strong> Please review the challenges below and provide guidance to ' + sName + '.</div>',
          '            <div class="detail-section">',
          '                <div class="detail-section-title">📋 Personal Performance Details</div>',
          '                <div class="detail-grid">',
          '                    <div class="detail-item"><div class="label">Performance Name</div><div class="value">' + tName + '</div></div>',
          '                    <div class="detail-item"><div class="label">Week Ending</div><div class="value">' + formattedWeek + '</div></div>',
          '                    <div class="detail-item" style="grid-column: 1 / -1;"><div class="label">Objective</div><div class="value">' + oName + '</div></div>',
          '                    <div class="detail-item"><div class="label">Progress</div><div class="value">' + prog + '%</div></div>',
          '                    <div class="detail-item"><div class="label">Status</div><div class="value"><span class="status-badge">' + (stat || 'Pending') + '</span></div></div>',
          '                </div>',
          '            </div>',
          '            <div class="detail-section" style="background: #fff5f5; border-color: #fed7d7;">',
          '                <div class="detail-section-title" style="color: #c53030;">⚠️ Challenges Reported</div>',
          '                <div class="challenge-box"><div class="challenge-text">"' + (chall || 'No specific challenges detailed') + '"</div></div>',
          '            </div>',
          '            <div class="detail-section">',
          '                <div class="detail-section-title">📌 Additional Context</div>',
          '                <div class="detail-grid">',
          '                    <div class="detail-item" style="grid-column: 1 / -1;"><div class="label">Achievements</div><div class="value">' + ach + '</div></div>',
          '                    <div class="detail-item" style="grid-column: 1 / -1;"><div class="label">Next Steps</div><div class="value">' + nSteps + '</div></div>',
          '                </div>',
          '            </div>',
          '            <div style="text-align: center;">',
          '                <a href="' + window.location.origin + '/ppn/manager" class="button">👀 Review in PMS</a>',
          '            </div>',
          '        </div>',
          '        <div class="footer">',
          '            <div>© ' + new Date().getFullYear() + ' Pannecia PMS. All rights reserved.</div>',
          '            <div class="footer-links">',
          '                <a href="' + window.location.origin + '/help">Help Center</a>',
          '                <a href="mailto:support@pannecia.com">Contact Support</a>',
          '                <a href="' + window.location.origin + '/privacy">Privacy Policy</a>',
          '            </div>',
          '            <div style="margin-top: 8px; font-size: 12px; color: #a0aec0;">This is an automated notification from the Performance Management System.</div>',
          '        </div>',
          '    </div>',
          '</body>',
          '</html>'
        ].join('\n');
      };

      // ✅ Build HTML version - this uses the recipientName defined above
      const emailHtml = buildChallengeEmailHTML({
        recipientName: recipientName,
        senderName: currentUserName,
        taskName: taskName,
        selectedWeek: selectedWeek,
        objectiveName: objectiveName,
        progress: progressText,
        status: statusText,
        challenges: challengesText,
        achievements: achievements,
        nextSteps: nextSteps
      });

      // ====== SEND EMAIL VIA NODE.JS BACKEND ======
      try {
        console.log('📧 [EMAIL] Sending challenge notification via Node.js backend...');
        console.log('📤 [EMAIL] To:', recipient.email);
        console.log('📤 [EMAIL] Subject:', emailSubject);

        // ✅ Send via Node.js backend API with HTML
        const response = await fetch(`${API_BASE_URL}/api/email/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            to: recipient.email,
            subject: emailSubject,
            message: emailMessage,
            html: emailHtml
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to send email via backend');
        }

        const result = await response.json();
        console.log('✅ [EMAIL] Challenge notification sent successfully:', result);

        // Mark as sent with cooldown (15 minutes)
        markChallengeEmailSent(emailKey, 15 * 60 * 1000);

        return {
          success: true,
          message: 'Email sent successfully',
          recipientName: recipientName,
          recipientEmail: recipient.email,
          recipientId: recipient.id,
          method: 'backend_email',
          source: recipientSource,
          response: result
        };

      } catch (apiError) {
        console.error('❌ [EMAIL] Backend email API failed:', apiError);

        // ✅ FALLBACK: Try DHIS2 message conversations as last resort
        console.log('📧 [EMAIL] Attempting fallback via message conversations...');

        try {
          const fallbackResult = await engine.mutate({
            resource: 'messageConversations',
            type: 'create',
            data: {
              subject: emailSubject,
              text: emailMessage,
              users: [{ id: recipient.id }],
              organisationUnits: [],
              userGroups: []
            }
          });

          console.log('✅ [EMAIL] Fallback message sent via DHIS2:', fallbackResult);

          // Mark as sent with cooldown (shorter cooldown for fallback)
          markChallengeEmailSent(emailKey, 5 * 60 * 1000);

          return {
            success: true,
            message: 'Email sent via fallback (DHIS2 internal messaging)',
            recipientName: recipientName,
            recipientEmail: recipient.email,
            recipientId: recipient.id,
            method: 'message_conversations_fallback',
            source: recipientSource,
            warning: 'Sent via internal messaging. User may not receive external email.',
            response: fallbackResult
          };
        } catch (fallbackError) {
          console.error('❌ [EMAIL] Fallback also failed:', fallbackError);
          return {
            success: false,
            error: 'All email methods failed',
            recipientName: recipientName,
            recipientEmail: recipient.email,
            source: recipientSource,
            warning: `Please notify ${recipientName} manually.`
          };
        }
      }

    } catch (error) {
      console.error('❌ [EMAIL] Critical error in notification process:', error);
      console.error('❌ [EMAIL] Error stack:', error.stack);

      return {
        success: false,
        error: error.message,
        stack: error.stack,
        warning: 'Email notification failed'
      };
    }
  };


  // Update this to allow editing existing tasks in past weeks
  const isFormDisabled = useCallback(() => {
    return currentTask?.sealed === true && Boolean(currentTask?.eventId);
  }, [currentTask?.sealed, currentTask?.eventId]);

  // Allow editing existing tasks even in past weeks
  const canEditTask = !isFormDisabled;

  // Generate draft ID for dataStore
  const getDraftId = (objectiveId, selectedWeek) => {
    return `${DRAFT_NAMESPACE}-${objectiveId}-${selectedWeek}`;
  };

  const syncTasksToDatabase = async (tasksData, objective, selectedWeek) => {
    try {
      const validTasks = tasksData.filter(
        (task) =>
          task.taskName &&
          task.taskName.trim() !== '' &&
          task.taskName !== 'Unnamed Personal Performance'
      );

      if (validTasks.length === 0) {
        console.log('⏸️ No valid tasks to sync to database');
        return { successCount: 0, errorCount: 0, eventIdMap: {} };
      }

      const existingEvents = await getExistingEvents(objective.id, selectedWeek);
      console.log('📋 Existing events for update:', existingEvents.map((e) => ({ id: e.event })));

      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      const eventIdMap = {};   // { localIdOrName: realEventId }

      for (const task of validTasks) {
        console.log('🔍 Syncing valid task:', {
          taskName: task.taskName,
          hasKpiId: !!task.kpiId,
          hasStatus: !!task.status,
          progress: task.progressPercent,
        });

        try {
          const r = await createOrUpdateTaskEvent(task, objective, selectedWeek, existingEvents);

          // Build the mapping key. Prefer local `id`, fall back to `taskName`.
          const localKey = task.id || task.taskName;
          if (r?.eventId) {
            eventIdMap[localKey] = r.eventId;
          }

          successCount++;
          console.log(`✅ Synced task: "${task.taskName}" (eventId=${r?.eventId || 'existing'})`);
        } catch (error) {
          console.error(`❌ Failed to sync task "${task.taskName}":`, error);
          errorCount++;
          errors.push(error.message);
        }
      }

      console.log(`📊 Sync Results: ${successCount} successful, ${errorCount} failed`);
      console.log('🗺️ eventIdMap:', eventIdMap);

      return { successCount, errorCount, eventIdMap };
    } catch (error) {
      console.error('❌ Error syncing tasks to database:', error);
      return { successCount: 0, errorCount: tasksData.length, eventIdMap: {} };
    }
  };

  const createOrUpdateTaskEvent = async (task, objective, selectedWeek, existingEvents) => {
    let objectivePositionTei = objective.trackedEntityInstance;
    if (isManagerView && selectedTeamMemberTEI) {
      objectivePositionTei = selectedTeamMemberTEI;
    }

    try {
      const dataValues = prepareDataValues(task, objective, selectedWeek, objectivePositionTei);

      if (task.eventId) {
        console.log(`🔄 Updating existing task by ID: ${task.eventId}`);
        const r = await updateExistingEvent(task.eventId, dataValues, task);
        return { ...r, eventId: task.eventId };
      }

      const existingEvent = findExistingEvent(task, existingEvents);
      if (existingEvent) {
        console.log(`🔄 Updating existing task by name: ${existingEvent.event}`);
        const r = await updateExistingEvent(existingEvent.event, dataValues, task);
        return { ...r, eventId: existingEvent.event };
      }

      console.log(`✨ Creating new task for ${selectedWeek}: "${task.taskName}"`);
      const r = await createNewEvent(objective, dataValues, objectivePositionTei, task);
      return r;   // already has eventId
    } catch (error) {
      handleTaskError(error, task);
      throw error;
    }
  };


  const prepareDataValues = (task, objective, selectedWeek, objectivePositionTei) => {
    // Map frontend status to backend status
    const backendStatus = mapFrontendToBackendStatus(task.status);

    const dataValues = [
      { dataElement: TASK_DATAELEMENT_MAPPING.selectedWeek, value: selectedWeek },
      { dataElement: TASK_DATAELEMENT_MAPPING.progressPercent, value: task.progressPercent?.toString() || '0' },
      { dataElement: TASK_DATAELEMENT_MAPPING.status, value: backendStatus },
      { dataElement: TASK_DATAELEMENT_MAPPING.achievements, value: task.achievements || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.challenges, value: task.challenges || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.nextSteps, value: task.nextSteps || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.objectiveId, value: objective.id },
      { dataElement: TASK_DATAELEMENT_MAPPING.taskName, value: task.taskName || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.taskDescription, value: task.taskDescription || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.kpiId, value: task.kpiId || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.positionTei, value: objectivePositionTei },
      { dataElement: TASK_DATAELEMENT_MAPPING.shareWithManager, value: task.shareWithManager === true ? 'true' : 'false' },
      { dataElement: TASK_DATAELEMENT_MAPPING.sealed, value: task.sealed === true ? 'true' : 'false' },
      { dataElement: TASK_DATAELEMENT_MAPPING.stretchTier, value: task.stretchTier || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.stretchCategories, value: Array.isArray(task.stretchCategories) ? task.stretchCategories.join(',') : (task.stretchCategories || '') },
      { dataElement: TASK_DATAELEMENT_MAPPING.stretchEvidence, value: task.stretchEvidence || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.touched, value: 'true' },
    ].filter(dv => dv.value !== undefined && dv.value !== null);

    console.log('🔍 [SHARE DEBUG]', {
      taskName: task.taskName,
      shareWithManager: task.shareWithManager,
      mappedUid: TASK_DATAELEMENT_MAPPING.shareWithManager,
      willWrite: task.shareWithManager === true ? 'true' : 'false',
    });

    return dataValues;
  };


  // Helper function 2: Find existing event - IMPROVED
  const findExistingEvent = (task, existingEvents) => {
    // First try to find by event ID if it exists
    if (task.eventId) {
      const eventById = existingEvents.find(event => event.event === task.eventId);
      if (eventById) {
        console.log('🔄 Found existing event by ID:', eventById.event);
        return eventById;
      }
    }

    // Then try by task name
    const existingEvent = existingEvents.find(event => {
      const taskNameValue = event.dataValues?.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value;
      return taskNameValue === (task.taskName || '');
    });

    if (existingEvent) {
      console.log('🔄 Found existing event by name:', existingEvent.event);
    }

    return existingEvent;
  };

  // Helper function 3: Update existing event
  const updateExistingEvent = async (eventId, dataValues, task) => {
    try {
      console.log('🔄 Attempting to update event:', eventId);
      console.log('📤 Update dataValues:', dataValues);
      console.log('📤 Full dataValues:', dataValues.map(dv => `${dv.dataElement}=${dv.value}`).join(', '));

      const result = await engine.mutate({
        resource: `events/${eventId}`,
        type: 'update',
        data: {
          program: PROGRAM_ID,
          programStage: TASK_PROGRAM_STAGE_ID,
          orgUnit: currentUserOrgUnitId,
          dataValues
        }
      });

      console.log(`✅ Successfully updated event for task: "${task.taskName || 'Unnamed Task'}"`);
      return { success: true, action: 'updated', eventId };

    } catch (error) {
      console.error(`❌ Failed to update event ${eventId} for task: "${task.taskName || 'Unnamed Task'}"`);

      // Enhanced error logging for updates
      if (error.details) {
        console.error('❌ Update error details:', {
          message: error.message,
          httpStatus: error.httpStatus,
          httpStatusCode: error.httpStatusCode
        });

        if (error.details.response) {
          console.error('❌ Update response:', error.details.response);

          if (error.details.response.importSummaries) {
            const importSummary = error.details.response.importSummaries[0];
            console.error('❌ Update import summary:', {
              status: importSummary?.status,
              description: importSummary?.description,
              conflicts: importSummary?.conflicts
            });
          }
        }
      }

      throw new Error(`Failed to update task "${task.taskName || 'Unnamed Task'}": ${error.message}`);
    }
  };


  // Helper function 4: Create new event
  const createNewEvent = async (objective, dataValues, objectivePositionTei, task) => {
    console.log('🎯 Creating task for objective:', objective.id, 'with position TEI:', objectivePositionTei);
    try {
      const enrollment = objective.enrollment;
      const eventData = {
        program: PROGRAM_ID,
        programStage: TASK_PROGRAM_STAGE_ID,
        orgUnit: currentUserOrgUnitId || objective.orgUnit,
        eventDate: new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
        trackedEntityInstance: objectivePositionTei,
        enrollment: enrollment,
        dataValues: dataValues,
      };

      console.log('🚀 Creating new event with payload:', {
        orgUnit: currentUserOrgUnitId || objective.orgUnit,
        trackedEntityInstance: objectivePositionTei,
        dataValuesCount: dataValues.length,
      });

      const result = await engine.mutate({
        resource: 'events',
        type: 'create',
        data: eventData,
      });

      // ── Extract the created event ID from the response ──
      const importSummary =
        result?.response?.importSummaries?.[0] ||
        result?.importSummaries?.[0] ||
        result?.bundleReport?.typeReportMap?.EVENT?.objectReports?.[0] ||
        null;

      const createdEventId =
        importSummary?.reference ||
        importSummary?.objectReports?.[0]?.uid ||
        importSummary?.imported ||   // some versions put the ID here
        null;

      console.log('✅ Successfully created event:', {
        taskName: task.taskName || 'Unnamed Personal Performance',
        createdEventId,
        rawResult: result,
      });

      return {
        success: true,
        action: 'created',
        eventId: createdEventId,      // ← propagate eventId up
        result,
      };
    } catch (error) {
      console.error(`❌ Failed to create event for task: "${task.taskName || 'Unnamed Personal Performance'}"`);

      // Handle warnings as success — but still try to extract the eventId
      if (error.details?.response?.importSummaries?.[0]?.importCount?.imported === 1) {
        const warningSummary = error.details.response.importSummaries[0];
        const createdEventId = warningSummary?.reference || warningSummary?.imported || null;

        console.log('⚠️ Event created with warnings:', {
          taskName: task.taskName || 'Unnamed Personal Performance',
          createdEventId,
          warnings: warningSummary?.conflicts,
        });

        return {
          success: true,
          action: 'created_with_warnings',
          eventId: createdEventId,   // ← propagate even on warnings
          warnings: true,
          result: error.details.response,
        };
      }

      // Enhanced error logging for creation
      if (error.details?.response) {
        const importSummary = error.details.response.importSummaries?.[0];
        console.error('❌ Creation error details:', {
          imported: importSummary?.importCount?.imported,
          updated: importSummary?.importCount?.updated,
          status: importSummary?.status,
          description: importSummary?.description,
          conflicts: importSummary?.conflicts,
        });
      }

      throw new Error(
        `Failed to create task "${task.taskName || 'Unnamed Personal Performance'}": ${error.message}`
      );
    }
  };

  // Helper function 5: Error handler
  const handleTaskError = (error, task) => {
    console.error(`💥 Critical error for task: "${task.taskName || 'Unnamed Personal Performance'}"`, {
      errorMessage: error.message,
      taskName: task.taskName,
      hasKPI: !!task.kpiId,
      progress: task.progressPercent
    });
  };

  // Get existing events for this objective and week
  const getExistingEvents = async (objectiveId, weekForFetch) => {
    try {
      let positionTeiToFilter = currentUserPosition?.trackedEntityInstance;
      if (isManagerView && selectedTeamMemberTEI) {
        positionTeiToFilter = selectedTeamMemberTEI;
      }

      if (!positionTeiToFilter || /^\d{4}-\d{2}-\d{2}$/.test(positionTeiToFilter)) {
        console.warn('⚠️ Invalid position TEI for getting existing events:', positionTeiToFilter);
        return [];
      }

      // Fetch by program + stage + trackedEntityInstance only.
      // Do NOT filter by data elements — it's unreliable.
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            orgUnit: currentUserOrgUnitId,
            trackedEntityInstance: positionTeiToFilter,
            fields: 'event,dataValues[dataElement,value],eventDate,lastUpdated',
            pageSize: 500,
            order: 'lastUpdated:desc'
          }
        }
      });

      const allEvents = response.events?.events || [];

      // Now filter in JS
      const matchingEvents = allEvents.filter((event) => {
        const dv = event.dataValues || [];
        const weekVal = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.selectedWeek)?.value;
        const posVal  = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.positionTei)?.value;
        const objVal  = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.objectiveId)?.value;

        if (posVal !== positionTeiToFilter) return false;
        if (weekVal !== weekForFetch) return false;
        if (objVal !== selectedObjective?.id) return false;

        return true;
      });

      console.log('📋 getExistingEvents → matched', {
        totalFetched: allEvents.length,
        matched: matchingEvents.length,
        objectiveId,
        weekForFetch,
        positionTeiToFilter
      });

      return matchingEvents;
    } catch (error) {
      console.error('Error fetching existing events:', error);
      return [];
    }
  };

  const refreshExistingTasksOnly = async (objectiveId, weekDate) => {
    try {
      let positionTeiToFilter = null;
      if (isManagerView && selectedTeamMemberTEI) {
        positionTeiToFilter = selectedTeamMemberTEI;
      } else if (currentUserPosition?.trackedEntityInstance) {
        positionTeiToFilter = currentUserPosition.trackedEntityInstance;
      }
      if (!positionTeiToFilter || /^\d{4}-\d{2}-\d{2}$/.test(positionTeiToFilter)) return;

      const response = await engine.query({
        tasks: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            orgUnit: currentUserOrgUnitId,
            trackedEntityInstance: positionTeiToFilter,
            fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value],lastUpdated',
            pageSize: 500,
            order: 'lastUpdated:desc',
          },
        },
      });

      const allEvents = response.tasks?.events || [];
      const matching = allEvents.filter((event) => {
        const dv = event.dataValues || [];
        const objVal = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.objectiveId)?.value;
        const weekVal = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.selectedWeek)?.value;
        const posVal = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.positionTei)?.value;
        return objVal === objectiveId && weekVal === weekDate && posVal === positionTeiToFilter;
      });

      const refreshed = matching.map((event) => {
        const dataValues = event.dataValues || [];
        const backendStatus =
          dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.status)?.value || 'pending';
        return {
          id: event.event,
          eventId: event.event,
          selectedWeek: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.selectedWeek)?.value || '',
          progressPercent: parseInt(dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.progressPercent)?.value) || 0,
          status: mapBackendToFrontendStatus(backendStatus),
          challenges: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.challenges)?.value || '',
          nextSteps: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.nextSteps)?.value || '',
          achievements: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.achievements)?.value || '',
          taskName: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value || '',
          taskDescription: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskDescription)?.value || '',
          kpiId: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.kpiId)?.value || '',
          objectiveId: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.objectiveId)?.value || '',
          isNew: false,
          shareWithManager: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.shareWithManager)?.value === 'true',
          managerComments: dataValues.find(dv => dv.dataElement === MANAGER_COMMENTS_DE)?.value || '',
          sealed: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.sealed)?.value === 'true',
          stretchTier: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.stretchTier)?.value || STRETCH_TIERS.NONE,
          stretchCategories: (dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.stretchCategories)?.value || '').split(',').filter(Boolean),
          stretchEvidence: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.stretchEvidence)?.value || '',
          touched: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.touched)?.value === 'true',
        };
      });

      // ✅ MERGE, don't replace
      setExistingTasks((prev) => {
        const incomingIds = new Set(refreshed.map((t) => t.eventId));
        const kept = prev.filter((t) => !incomingIds.has(t.eventId));
        return sortTasksDeterministically([...kept, ...refreshed]);
      });

      console.log('🔄 refreshExistingTasksOnly → merged', {
        refreshedCount: refreshed.length,
        previousCount: existingTasks.length,
      });
    } catch (err) {
      console.error('refreshExistingTasksOnly failed:', err);
    }
  };

  const saveDraft = async (tasksData, objective, selectedWeek) => {
    const allTasksComplete = tasksData.every((task) => task.progressPercent === 100);

    if (!objective) {
      console.log('Skipping auto-save: No objective');
      return;
    }

    const validTasks = tasksData.filter(
      (task) =>
        task.taskName && task.taskName.trim() !== '' && task.taskName !== 'Unnamed Personal Performance'
    );

    if (allTasksComplete && validTasks.length === 0) {
      console.log('Skipping final save: No task with a valid name');
      return;
    }

    if (validTasks.length === 0) {
      console.log('⏸️ Skipping auto-save: No task with a valid name');
      return;
    }

    try {
      setIsAutoSaving(true);
      const result = await syncTasksToDatabase(validTasks, objective, selectedWeek);

      // Bind any newly-created event IDs back into `tasks`
      if (result.eventIdMap && Object.keys(result.eventIdMap).length > 0) {
        setTasks((prev) =>
          prev.map((t) => {
            const keyById = t.id;
            const keyByName = t.taskName;
            const newEventId =
              (keyById && result.eventIdMap[keyById]) ||
              (keyByName && result.eventIdMap[keyByName]);

            if (newEventId && !t.eventId) {
              console.log(`🔗 Binding eventId ${newEventId} to local task "${t.taskName}"`);
              return { ...t, eventId: newEventId, id: newEventId, isNew: false };
            }
            return t;
          })
        );
      }

      if (result.successCount > 0) {
        await refreshExistingTasksOnly(objective.id, selectedWeek);
      }

      setLastAutoSaved(new Date());
      setHasUnsavedChanges(false);

      if (objective && result.successCount > 0) {
        // Force update touched status immediately after successful save
        const hasAnyContentAfterSave = validTasks.some(task => {
          const hasValidTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Personal Performance' && task.taskName !== '';
          const hasTaskDescription = task.taskDescription?.trim() && task.taskDescription.trim().length > 3;
          const hasAchievements = task.achievements?.trim() && task.achievements.trim().length > 5;
          const hasChallenges = hasMeaningfulChallenge(task.challenges);
          const hasActivities = task.subTasks && task.subTasks.length > 0;
          const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;

          return hasValidTaskName || hasTaskDescription || hasAchievements || hasChallenges || hasActivities || hasProgress;
        });

        console.log(`🔍 [SAVE] Force updating touched status for ${objective.name}: ${hasAnyContentAfterSave ? 'TOUCHED ✅' : 'NOT TOUCHED ⚠️'}`);

        setObjectiveDrafts(prev => ({
          ...prev,
          [objective.id]: hasAnyContentAfterSave
        }));
      }

      // Force refresh of objective drafts status - SET TO TRUE if any content exists
      const hasAnyContent = validTasks.some(task => {
        const hasActivities = task.subTasks?.length > 0;
        const hasProgress = task.progressPercent > 0;
        const hasAchievements = task.achievements?.trim()?.length > 0;
        const hasChallenges = hasMeaningfulChallenge(task.challenges);
        const hasTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Personal Performance' && task.taskName !== '';
        return hasActivities || hasProgress || hasAchievements || hasChallenges || hasTaskName;
      });

      // Immediately update touched status after successful save
      if (hasAnyContent && objective) {
        setObjectiveDrafts(prev => ({
          ...prev,
          [objective.id]: true
        }));
      }

      const incompleteCount = validTasks.filter(t => t.progressPercent < 100).length;
      const completeCount = validTasks.filter(t => t.progressPercent === 100).length;

      if (result.successCount > 0) {
        const weekType = isPastWeek(selectedWeek) ? 'past week' : 'current week';
        showNotification(
          `✓ Saved: ${result.successCount} Personal Performance Notes(s) updated in ${weekType}. ` +
          `${incompleteCount} in progress, ${completeCount} complete.`,
          'success'
        );
      } else if (validTasks.length > 0 && result.errorCount === 0) {
        showNotification('✅ Draft saved. Add a Personal Performance name to sync to database.', 'success');
      } else if (result.errorCount > 0) {
        showNotification(`⚠️ ${result.errorCount} Personal Performance(s) failed to sync. Progress saved to draft.`, 'warning');
      } else {
        showNotification('Progress saved to draft.', 'success');
      }

    } catch (error) {
      console.error('❌ Auto-save failed:', error);
      showNotification('Failed to save progress', 'error');
    } finally {
      setIsAutoSaving(false);
    }
  };

  // Updated  loadDraft to handle multiple tasks
  /*
  const loadDraft = async (objectiveId, selectedWeek) => {
    try {
      const draftId = getDraftId(objectiveId, selectedWeek);
      const response = await engine.query({
        draft: {
          resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        }
      });

      if (response.draft && response.draft.tasks && Array.isArray(response.draft.tasks)) {
        // Validate that tasks have the expected structure
        const validTasks = response.draft.tasks.map(task => ({
          id: task.id || Date.now().toString(),
          taskName: task.taskName || "",
          taskDescription: task.taskDescription || "",
          progressPercent: task.progressPercent || 0,
          status: task.status || "",
          achievements: task.achievements || "",
          challenges: task.challenges || "",
          nextSteps: task.nextSteps || "",
          kpiId: task.kpiId || "",
          kpiName: task.kpiName || "",
          isNew: task.isNew !== undefined ? task.isNew : true,
          eventId: task.eventId || "",
          subTasks: task.subTasks || [],
          sealed: task.sealed ?? false,
        }));

        setTasks(validTasks);
        setLastAutoSaved(new Date(response.draft.lastSaved));
        setHasUnsavedChanges(false);
        showNotification('Loaded auto-saved Personal Performances from your previous session', 'success');
        return true;
      }
    } catch (error) {
      // ✅ EXPECTED: No draft exists yet, this is normal
      console.log('No draft found for this objective/week - this is normal');
    }
    return false;
  };
  */

  // Delete draft (when form is submitted or cleared)
  const deleteDraft = async (objectiveId, selectedWeek) => {
    try {
      const draftId = getDraftId(objectiveId, selectedWeek);
      await engine.mutate({
        resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        type: 'delete',
      });
    } catch (error) {
      console.log('No draft to delete or delete failed');
    }
  };

  // Check if a task already exists for this week
  const checkExistingTaskThisWeek = async (kpiId, positionTeiToFilter = null) => {
    try {
      if (!positionTeiToFilter) {
        if (isManagerView && selectedTeamMemberTEI) {
          positionTeiToFilter = selectedTeamMemberTEI;
        } else if (currentUserPosition?.trackedEntityInstance) {
          positionTeiToFilter = currentUserPosition.trackedEntityInstance;
        }
      }

      const isDateString = /^\d{4}-\d{2}-\d{2}$/.test(positionTeiToFilter);
      if (!positionTeiToFilter || isDateString) return false;

      const response = await engine.query({
        tasks: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            filter: [
              `${TASK_DATAELEMENT_MAPPING.kpiId}:eq:${kpiId}`,
              `${TASK_DATAELEMENT_MAPPING.selectedWeek}:eq:${selectedWeek}`,
              `${TASK_DATAELEMENT_MAPPING.positionTei}:eq:${positionTeiToFilter}`,
            ],
            fields: 'event',
            pageSize: 1,
          },
        },
      });
      return response.tasks?.events?.length > 0;
    } catch (error) {
      console.error('Error checking existing tasks:', error);
      return false;
    }
  };

  useEffect(() => {
    tasks.forEach(task => {
      const taskKey = task.eventId || task.id;
      if (!taskKey) return;

      const challenge = task.challenges || '';
      if (!(taskKey in challengeBaselineByTaskRef.current)) {
        challengeBaselineByTaskRef.current[taskKey] = challenge;
      }
      if (!(taskKey in challengeNotifiedByTaskRef.current)) {
        challengeNotifiedByTaskRef.current[taskKey] = challenge;
      }
    });
  }, [tasks]);

  const saveOnBlur = useCallback(async () => {
    const obj = selectedObjectiveRef.current;
    if (!obj) return;
    if (isFormDisabled()) return;

    const currentTasks = tasksRef.current || [];
    if (currentTasks.length === 0) return;

    const hasContent = currentTasks.some((task) => {
      const hasTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Personal Performance';
      const hasDescription = task.taskDescription?.trim()?.length > 0;
      const hasAchievements = task.achievements?.trim()?.length > 0;
      const hasChallenges = task.challenges?.trim()?.length > 0;
      const hasNextSteps = task.nextSteps?.trim()?.length > 0;
      return hasTaskName || hasDescription || hasAchievements || hasChallenges || hasNextSteps;
    });
    if (!hasContent) return;

    // Note: we do NOT bail on `allTasksComplete` because with multiple PPNs
    // the current task may be complete while siblings aren't, and we still want to persist.

    try {
      await saveDraft(currentTasks, obj, selectedWeekRef.current);
    } catch (err) {
      console.error('❌ saveOnBlur failed:', err);
    }
  }, [isFormDisabled]);

  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges && selectedObjective) {
        // Best-effort synchronous save — DHIS2 engine may not support sync
        // You may need to use navigator.sendBeacon or just rely on beforeunload prompt
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges, selectedObjective]);



  // Add this useEffect to detect when tasks are loaded from the database
  useEffect(() => {
    if (!selectedObjective) return;
    if (tasks.length === 0) return;

    // Check if any task has meaningful content
    const hasContent = tasks.some(task => {
      const hasValidTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Personal Performance' && task.taskName !== '';
      const hasTaskDescription = task.taskDescription?.trim() && task.taskDescription.trim().length > 3;
      const hasAchievements = task.achievements?.trim() && task.achievements.trim().length > 5;
      const hasChallenges = hasMeaningfulChallenge(task.challenges);
      const hasActivities = task.subTasks && task.subTasks.length > 0;
      const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;

      return hasValidTaskName || hasTaskDescription || hasAchievements || hasChallenges || hasActivities || hasProgress;
    });

    console.log(`🔍 [LOAD] Initial content check for ${selectedObjective.name}: ${hasContent ? 'HAS CONTENT ✅' : 'NO CONTENT ⚠️'}`);

    setObjectiveDrafts(prev => {
      const current = prev[selectedObjective.id] || false;
      if (current !== hasContent) {
        return { ...prev, [selectedObjective.id]: hasContent };
      }
      return prev;
    });
  }, [selectedObjective, tasks]); // This will run whenever tasks change

  // Calculate task progress from subtasks
  const calculateTaskProgress = (subTasks) => {
    if (!subTasks || subTasks.length === 0) return 0;
    const totalProgress = subTasks.reduce((sum, subTask) => sum + (subTask.progressPercent || 0), 0);
    return Math.round(totalProgress / subTasks.length);
  };


  const handleSearchChange = ({ value }) => {
    setSearchTerm(value);
  };


  const debugPositionTEIAssignment = (tasks, objective) => {
    if (process.env.NODE_ENV !== 'development') return;
    console.log('🔍 POSITION TEI DEBUG - Task Creation:');
    console.log('Objective:', {
      id: objective.id,
      name: objective.name,
      trackedEntityInstance: objective.trackedEntityInstance,
      hasPositionTEI: !!objective.trackedEntityInstance
    });

    tasks.forEach((task, index) => {
      console.log(`Task ${index + 1}: "${task.taskName}"`, {
        willGetPositionTEI: objective.trackedEntityInstance,
        hasRequiredData: !!(task.taskName && task.kpiId && task.status)
      });
    });
  };

  // Add this debug function to verify task creation
  // Add this debug function to verify task creation
  /*
const verifyTaskCreation = async (objectiveId, taskName) => {
  try {
    const response = await engine.query({
      events: {
        resource: 'events',
        params: {
          program: PROGRAM_ID,
          programStage: TASK_PROGRAM_STAGE_ID,
          filter: [`plJovUlxfQ0:eq:${objectiveId}`],
          fields: 'event,dataValues[dataElement,value]'
        }
      }
    });

    const events = response.events?.events || [];

    const newTask = events.find(event => {
      const taskNameValue = event.dataValues?.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value;
      return taskNameValue === taskName;
    });

    if (newTask) {
      const positionTei = newTask.dataValues?.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.positionTei)?.value;
      console.log('✅ VERIFIED: New task created with Position TEI:', {
        taskName: taskName,
        positionTei: positionTei,
        hasPositionTei: !!positionTei
      });
    } else {
      console.log('❌ TASK NOT FOUND: Task may not have been created');
    }
  } catch (error) {
    console.error('Error verifying task creation:', error);
  }
};
  */

  // Update the objective selection
  const handleObjectiveSelect = async (objective) => {
    lastSeedKeyRef.current = '';   // allow the seed to re-run for the new objective
    setSelectedObjective(objective);
    setSelectedKPI(null);
    setTasksLoading(true);

    debugPositionTEIAssignment(tasks, objective);

    // Determine which position TEI to use for filtering
    let positionTeiToFilter = null;

    // ✅ FIX: First check if we're in manager view with a team member selected
    if (isManagerView && selectedTeamMemberTEI) {
      positionTeiToFilter = selectedTeamMemberTEI;
      console.log('👥 [MANAGER VIEW] Filtering tasks for team member:', selectedTeamMemberTEI);
    } else if (currentUserPosition?.trackedEntityInstance) {
      // ✅ FIX: Use the current user's position TEI
      positionTeiToFilter = currentUserPosition.trackedEntityInstance;
      console.log('👤 [USER VIEW] Filtering tasks for current user:', positionTeiToFilter);
    }

    // ✅ FIX: Validate position TEI - it should NOT be a date string
    const isDateString = /^\d{4}-\d{2}-\d{2}$/.test(positionTeiToFilter);
    if (!positionTeiToFilter || isDateString) {
      console.error('❌ Invalid position TEI:', positionTeiToFilter);
      showNotification('Unable to load tasks: Invalid position', 'error');
      return;
    }

    // Fetch KPIs for this objective
    await fetchKPIsForObjective(objective.id);

    // Update the draft status for this objective
    const hasDraft = await checkObjectiveHasDraft(objective.id, selectedWeek);
    setObjectiveDrafts(prev => ({
      ...prev,
      [objective.id]: hasDraft
    }));
  };

  const createEmptyTask = (kpiId = '', kpiName = '') => ({
    id: Date.now().toString(),
    taskName: '',
    taskDescription: '',
    progressPercent: 0,
    status: 'pending',
    achievements: '',
    challenges: '',
    nextSteps: '',
    kpiId,
    kpiName,
    isNew: true,
    eventId: '',
    shareWithManager: false,
    sealed: false,
    stretchTier: STRETCH_TIERS.NONE,
    stretchCategories: [],
    stretchEvidence: '',
  });

  // Load PPNs for the current KPI + week.
  // The function now only accepts the week date — scope is KPI + week + position.
  const loadExistingTasksForWeek = async (weekDate = selectedWeek) => {
    setTasksLoading(true);

    try {
      console.log(`🔍 Loading PPNs for KPI ${selectedKPI?.eventId || '(none)'}, week ${weekDate}`);

      let positionTeiToFilter = null;
      if (isManagerView && selectedTeamMemberTEI) {
        positionTeiToFilter = selectedTeamMemberTEI;
      } else if (currentUserPosition?.trackedEntityInstance) {
        positionTeiToFilter = currentUserPosition.trackedEntityInstance;
      }

      if (!positionTeiToFilter || /^\d{4}-\d{2}-\d{2}$/.test(positionTeiToFilter)) {
        console.warn('⚠️ Invalid position TEI for loading tasks:', positionTeiToFilter);
        setTasks([createEmptyTask(selectedKPI?.eventId, selectedKPI?.name)]);
        setHasSubmittedTaskThisWeek(false);
        setShowTaskForm(true);
        return;
      }

      const response = await engine.query({
        tasks: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            orgUnit: currentUserOrgUnitId,
            trackedEntityInstance: positionTeiToFilter,
            fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value],lastUpdated',
            pageSize: 500,
            order: 'lastUpdated:desc',
          },
        },
      });

      const allEvents = response.tasks?.events || [];
      console.log(`🔍 loadExistingTasksForWeek → fetched ${allEvents.length} events for TEI`);

      const matchingEvents = allEvents.filter((event) => {
        const dv = event.dataValues || [];
        const weekVal = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.selectedWeek)?.value;
        const posVal = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.positionTei)?.value;
        const kpiVal = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.kpiId)?.value;

        if (posVal !== positionTeiToFilter) return false;
        if (weekVal !== weekDate) return false;

        // If a KPI is selected, require an exact KPI match.
        // Otherwise return any PPN for this position + week.
        if (selectedKPI?.eventId) {
          return kpiVal === selectedKPI.eventId;
        }
        return true;
      });

      console.log(
        `✅ Matched ${matchingEvents.length} PPN(s) for KPI ${selectedKPI?.eventId || '(any)'}, week ${weekDate}`
      );

      if (matchingEvents.length > 0) {
        const tasksData = matchingEvents.map((event) => {
          const dataValues = event.dataValues || [];
          const backendStatus =
            dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.status)?.value || 'pending';
          const frontendStatus = mapBackendToFrontendStatus(backendStatus);
          const kpiId = dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.kpiId)?.value || '';
          const kpi = kpis.find((k) => k.eventId === kpiId);

          return {
            id: event.event,
            eventId: event.event,
            taskName:
              dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value ||
              'Unnamed Personal Performance',
            taskDescription:
              dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskDescription)?.value || '',
            progressPercent:
              Number(
                dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.progressPercent)?.value
              ) || 0,
            status: frontendStatus,
            achievements:
              dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.achievements)?.value || '',
            challenges:
              dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.challenges)?.value || '',
            nextSteps:
              dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.nextSteps)?.value || '',
            kpiId,
            kpiName: kpi?.name || '',
            isNew: false,
            shareWithManager:
              dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.shareWithManager)?.value === 'true',
            sealed: dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.sealed)?.value === 'true',
            stretchTier:
              dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.stretchTier)?.value ||
              STRETCH_TIERS.NONE,
            stretchCategories: (
              dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.stretchCategories)?.value || ''
            )
              .split(',')
              .filter(Boolean),
            stretchEvidence:
              dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.stretchEvidence)?.value || '',
            touched: dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.touched)?.value === 'true',
          };
        });

        const sortedTasksData = sortTasksDeterministically(tasksData);

        setTasks(sortedTasksData);
        setSelectedTaskIndex(0);
        setHasSubmittedTaskThisWeek(true);
        setShowTaskForm(true);
      } else {
        setTasks([createEmptyTask(selectedKPI?.eventId, selectedKPI?.name)]);
        setSelectedTaskIndex(0);
        setHasSubmittedTaskThisWeek(false);
        setShowTaskForm(true);
      }
    } catch (error) {
      console.error('❌ Error loading existing tasks:', error);
      showNotification('Failed to load Personal Performances for this week', 'error');
    } finally {
      setTasksLoading(false);
    }
  };

  const handleClearObjective = async () => {
    if (selectedObjective) {
      setObjectiveDrafts(prev => ({
        ...prev,
        [selectedObjective.id]: false
      }));
    }

    setSelectedObjective(null);
    setHasSubmittedTaskThisWeek(false);
    setHasUnsavedChanges(false);
    setKpiEnforcementActive(false); // Reset enforcement
    setExistingTasks([]);
    setSelectedTaskIndex(0);
  };

  useEffect(() => {
    const fetchUser = async () => {
      const userInfo = await getCurrentUserWithRole(engine);
      setCurrentUser(userInfo);
    };

    fetchUser();
  }, [engine]);



  // ─────────────────────────────────────────────────────────────────────────
  // PART B — Sync tasks[] with existingTasks whenever existingTasks refreshes.
  //          - If tasks[] are placeholders (no eventId) and existingTasks has matches → replace.
  //          - Otherwise → for each task with an eventId, copy status + progressPercent
  //            from the fresh existingTasks record into the task.
  //          - Never touches text fields (taskName, achievements, challenges, nextSteps)
  //            so mid-edit textareas aren't reset.
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (currentStep !== 'FILL_TASK') return;
    if (!selectedKPI?.eventId) return;
    if (!selectedObjective) return;

    // Guard: skip if the position hasn't loaded yet. The loader will no-op
    // without a valid position, and we don't want to seed a blank task that
    // immediately gets replaced.
    const positionTei = isManagerView && selectedTeamMemberTEI
      ? selectedTeamMemberTEI
      : currentUserPosition?.trackedEntityInstance;

    if (!positionTei || /^\d{4}-\d{2}-\d{2}$/.test(positionTei)) return;

    // Dedup: only run when the KPI or week actually changed since last load.
    const loadKey = `${selectedKPI.eventId}::${selectedWeek}`;
    if (lastSeedKeyRef.current === loadKey) return;
    lastSeedKeyRef.current = loadKey;

    // The loader owns `tasks`. It fetches, filters by KPI + week, and sets state.
    loadExistingTasksForWeek(selectedWeek);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedKPI?.eventId,
    selectedWeek,
    currentStep,
    selectedObjective?.id,
    currentUserPosition?.trackedEntityInstance,
    selectedTeamMemberTEI,
    isManagerView,
  ]);

  // Load current user position for manager lookup
  useEffect(() => {
    const loadPosition = async () => {
      if (!currentUser?.id || !engine) return;

      try {
        console.log('🔍 [POSITION] Loading position for user:', currentUser.id);

        const response = await engine.query({
          currentUserPosition: {
            resource: 'trackedEntityInstances',
            params: {
              trackedEntityType: 'UGGo0Ebjl0E', // Position TE type
              filter: `V66IFsw5kq6:eq:${currentUser.id}`, // Current Incumbent Staff ID
              fields: 'trackedEntityInstance,attributes[attribute,value]',
              ouMode: 'ACCESSIBLE',
              pageSize: 1
            }
          }
        });

        const position = response.currentUserPosition?.trackedEntityInstances?.[0];
        if (position) {
          setCurrentUserPosition(position);
          console.log('✅ [POSITION] Loaded user position:', position.trackedEntityInstance);
        } else {
          console.warn('⚠️ [POSITION] No position found for user:', currentUser.id);
          // Set an empty position to prevent null errors
          setCurrentUserPosition({ trackedEntityInstance: null });
        }
      } catch (error) {
        console.error('❌ [POSITION] Failed to load position:', error);
        // Set an empty position to prevent null errors
        setCurrentUserPosition({ trackedEntityInstance: null });
      }
    };

    loadPosition();
  }, [currentUser?.id, engine]);


  useEffect(() => {
    const taskKey = currentTask?.eventId || currentTask?.id || '';
    console.log('🔄 [CHALLENGE TASK] Personal Performance selected', {
      selectedTaskIndex,
      currentTaskId: currentTask?.id,
      currentTaskEventId: currentTask?.eventId,
      currentTaskStatus: currentTask?.status,
      currentTaskProgress: currentTask?.progressPercent,
      currentTaskName: currentTask?.taskName,
      currentTaskWeek: currentTask?.selectedWeek,
      currentTaskKpiId: currentTask?.kpiId,
      selectedKpiId: selectedKPI?.eventId,
      selectedWeek,
      siblingTasks: tasks.map((t) => ({
        id: t.eventId || t.id,
        name: t.taskName,
        status: t.status,
        progress: t.progressPercent,
        week: t.selectedWeek,
        kpiId: t.kpiId,
      })),
    });
  }, [selectedTaskIndex, currentTask?.id, currentTask?.status, tasks]);


  // Fetch objectives
  useEffect(() => {
    const fetchObjectives = async () => {
      // ✅ FIX: Check if currentUserPosition exists before proceeding
      if (!currentUser || !fiscalYear.start || !fiscalYear.end || !currentUserPosition) {
        console.log('⏳ Waiting for currentUserPosition to load...', {
          hasCurrentUser: !!currentUser,
          // hasFiscalYear: fiscalYearLoaded,
          hasPosition: !!currentUserPosition,
          positionTei: currentUserPosition?.trackedEntityInstance
        });
        return;
      }

      // ✅ FIX: Validate position TEI - it should NOT be a date string
      const positionTei = currentUserPosition.trackedEntityInstance;
      const isDateString = /^\d{4}-\d{2}-\d{2}$/.test(positionTei);
      if (!positionTei || isDateString) {
        console.warn('⚠️ Invalid position TEI for fetching objectives:', positionTei);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Determine which position TEI to use for filtering
        let positionTeiToFilter = positionTei;

        // If manager view and a team member is selected, use their position TEI
        if (isManagerView && selectedTeamMemberTEI) {
          positionTeiToFilter = selectedTeamMemberTEI;
          console.log('👥 [MANAGER VIEW] Filtering objectives for team member:', selectedTeamMemberTEI);
        }

        console.log('🎯 [FETCH OBJECTIVES] Filtering by position TEI:', positionTeiToFilter);

        const response = await engine.query({
          objectives: {
            resource: 'events',
            params: {
              program: PROGRAM_ID,
              programStage: 'VIBtpvkpHFR',
              fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value],trackedEntityInstance,createdBy,createdByUserInfo,storedBy,lastUpdatedByUserInfo',
              pageSize: 1000,
              order: 'eventDate:desc',
              filter: [`SlgO60cMK3F:eq:${positionTeiToFilter}`]
            }
          }
        });


        const processObjectives = async () => {
          const processedObjectives = [];

          for (const event of response.objectives?.events || []) {
            const dataValues = event.dataValues || [];
            const eventPositionTei = dataValues.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;

            if (eventPositionTei !== positionTeiToFilter) {
              continue;
            }

            const usernameRaw =
              event.createdByUserInfo?.username ||
              event.storedBy ||
              event.lastUpdatedByUserInfo?.username ||
              null;

            const creatorUsername = usernameRaw ? usernameRaw.toLowerCase().trim() : '';

            const isVisible = shouldShowObjective(
              currentUser.roleId,
              currentUser.username,
              creatorUsername
            );

            if (isVisible) {
              const startDate =
                dataValues.find(dv => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.START_DATE)?.value ||
                event.eventDate;
              const completionDate =
                dataValues.find(dv => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.COMPLETION_DATE)?.value || '';

              processedObjectives.push({
                id: event.event,
                name: dataValues.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed Objective',
                description: dataValues.find(dv => dv.dataElement === 'nWqW5BrcgP1')?.value || '',
                orgUnit: event.orgUnit,
                orgUnitName: event.orgUnitName,
                trackedEntityInstance: event.trackedEntityInstance,
                startDate,
                completionDate,
                eventDate: event.eventDate,
                createdBy: creatorUsername,
                positionTei: eventPositionTei
              });
            }
          }

          const fiscalFilteredObjectives = processedObjectives.filter(objective =>
            objectiveBelongsToFiscalYear(objective, fiscalYear)
          );

          console.log('📊 [OBJECTIVES FETCH RESULTS]', {
            totalFromApi: response.objectives?.events?.length || 0,
            afterPositionFilter: processedObjectives.length,
            afterFiscalFilter: fiscalFilteredObjectives.length,
            positionTeiToFilter,
            isManagerView
          });

          return fiscalFilteredObjectives;
        };

        const visibleObjectives = await processObjectives();
        setObjectives(visibleObjectives);

      } catch (error) {
        console.error('Error fetching objectives:', error);
        showNotification('Failed to load objectives', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchObjectives();
  }, [currentUser, engine, fiscalYear, currentUserPosition, isManagerView, selectedTeamMemberTEI]);



  // Load status options
  useEffect(() => {
    const loadOptions = async () => {
      // fetchStatusOptions already sets statusOptions and returns the formatted array
      const options = await fetchStatusOptions();
      // No need to reformat - fetchStatusOptions already formats them properly
      // Just ensure statusOptions is set (it already is inside fetchStatusOptions)
      if (options && options.length > 0) {
        console.log('✅ Status options loaded:', options);
      }
    };
    loadOptions();
  }, []);

  // Load ALL existing tasks for this position (any objective, any week, any KPI).
// This is the persistent source of truth used by the objective badge ("TOUCHED"
// / "NOT TOUCHED"). It must NOT be scoped to a single objective, otherwise the
// badge can't tell whether a *different* objective has been touched.
//
// This effect does NOT touch `tasks` — that's the job of the KPI filter and
// loadExistingTasksForWeek. It only populates `existingTasks`.
useEffect(() => {
  // Determine the position TEI to query. Manager view uses the selected team
  // member; otherwise use the current user's own position.
  let positionTeiToFilter = null;
  if (isManagerView && selectedTeamMemberTEI) {
    positionTeiToFilter = selectedTeamMemberTEI;
  } else if (currentUserPosition?.trackedEntityInstance) {
    positionTeiToFilter = currentUserPosition.trackedEntityInstance;
  }

  // Bail if the position isn't available yet (or is a malformed date string).
  if (!positionTeiToFilter || /^\d{4}-\d{2}-\d{2}$/.test(positionTeiToFilter)) {
    setExistingTasks([]);
    return;
  }

  const loadExistingTasks = async () => {
    try {
      const response = await engine.query({
        tasks: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            orgUnit: currentUserOrgUnitId,
            trackedEntityInstance: positionTeiToFilter,
            fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value],lastUpdated',
            pageSize: 500,
            order: 'lastUpdated:desc',
          },
        },
      });

      const allEvents = response.tasks?.events || [];

      // Keep every event for this position — regardless of objective.
      // Objective scoping happens at render time via
      //   existingTasks.filter(t => t.objectiveId === objective.id)
      const relevantEvents = allEvents.filter((event) => {
        const dv = event.dataValues || [];
        const posVal = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.positionTei)?.value;

        // Must belong to this position
        if (posVal !== positionTeiToFilter) return false;

        // Manager view still requires shareWithManager
        if (isManagerView) {
          const sharedVal = dv.find((x) => x.dataElement === TASK_DATAELEMENT_MAPPING.shareWithManager)?.value;
          if (sharedVal !== 'true') return false;
        }

        return true;
      });

      const existingTasksData = relevantEvents.map((event) => {
        const dataValues = event.dataValues || [];
        const backendStatus =
          dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.status)?.value || 'pending';
        const frontendStatus = mapBackendToFrontendStatus(backendStatus);

        return {
          id: event.event,
          eventId: event.event,
          selectedWeek:
            dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.selectedWeek)?.value ||
            'Unknown date',
          progressPercent:
            parseInt(dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.progressPercent)?.value) || 0,
          status: frontendStatus,
          challenges: dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.challenges)?.value || '',
          nextSteps: dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.nextSteps)?.value || '',
          achievements: dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.achievements)?.value || '',
          taskName: dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value || '',
          taskDescription:
            dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskDescription)?.value || '',
          kpiId: dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.kpiId)?.value || '',
          objectiveId: dataValues.find((dv) => dv.dataElement === TASK_DATAELEMENT_MAPPING.objectiveId)?.value || '',
          isNew: false,
          shareWithManager: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.shareWithManager)?.value === 'true',
          sealed: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.sealed)?.value === 'true',
          stretchTier: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.stretchTier)?.value || STRETCH_TIERS.NONE,
          stretchCategories: (dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.stretchCategories)?.value || '').split(',').filter(Boolean),
          stretchEvidence: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.stretchEvidence)?.value || '',
          // ── NEW: persisted "touched" flag ──
          touched: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.touched)?.value === 'true',
        };
      });

      // Deterministic ordering so the same task is always at index 0 for a given week+KPI
      existingTasksData.sort((a, b) => {
        const weekCmp = (b.selectedWeek || '').localeCompare(a.selectedWeek || '');
        if (weekCmp !== 0) return weekCmp;
        return (a.taskName || '').localeCompare(b.taskName || '');
      });

      console.log('📋 loadExistingTasks (broad) matched', {
        totalFromApi: allEvents.length,
        relevant: existingTasksData.length,
        positionTeiToFilter,
        tasks: existingTasksData.map((t) => ({
          id: t.id,
          name: t.taskName,
          week: t.selectedWeek,
          status: t.status,
          progress: t.progressPercent,
          kpiId: t.kpiId,
          objectiveId: t.objectiveId,
          touched: t.touched,
        })),
      });

      setExistingTasks(existingTasksData);
    } catch (error) {
      console.error('Error loading existing tasks (broad):', error);
      setExistingTasks([]);
    }
  };

  loadExistingTasks();
  // Note: `selectedObjective` is intentionally NOT a dependency.
  // The broad loader fetches everything for the position, so it doesn't need
  // to re-run when the user picks a different objective.
}, [
  engine,
  currentUserPosition?.trackedEntityInstance,
  selectedTeamMemberTEI,
  currentUserOrgUnitId,
  isManagerView,
]);

  useEffect(() => {
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (window.notificationTimeout) {
        clearTimeout(window.notificationTimeout);
      }
    };
  }, []);

  // ============================================================================
  // FIX: Update checkObjectivesForTasksAndDrafts - Pass correct position TEI
  // ============================================================================
  useEffect(() => {
    const checkObjectivesForTasksAndDrafts = async () => {
      if (objectives.length === 0) {
        console.log('📭 No objectives to check');
        return;
      }

      let positionTeiToFilter = null;
      if (isManagerView && selectedTeamMemberTEI) {
        positionTeiToFilter = selectedTeamMemberTEI;
      } else if (currentUserPosition?.trackedEntityInstance) {
        positionTeiToFilter = currentUserPosition.trackedEntityInstance;
      }

      const isDateString = /^\d{4}-\d{2}-\d{2}$/.test(positionTeiToFilter);
      if (!positionTeiToFilter || isDateString) {
        console.warn('⚠️ Invalid position TEI for checking objectives:', positionTeiToFilter);
        return;
      }

      console.log('🔍 Checking objectives with position TEI:', positionTeiToFilter);

      const tasksMap = {};
      const draftsMap = {};

      for (const objective of objectives) {
        try {
          const hasTask = await checkExistingTaskThisWeek(objective.id, positionTeiToFilter);
          tasksMap[objective.id] = hasTask;

          if (!hasTask) {
            const hasDraft = await checkObjectiveHasDraft(objective.id, selectedWeek);
            draftsMap[objective.id] = hasDraft;
          } else {
            draftsMap[objective.id] = false;
          }
        } catch (error) {
          console.error(`Error checking task/draft for objective ${objective.id}:`, error);
          tasksMap[objective.id] = false;
          draftsMap[objective.id] = false;
        }
      }

      setObjectiveTasks(tasksMap);
      setObjectiveDrafts(prev => {
        const newDrafts = { ...prev };
        for (const [id, hasDraft] of Object.entries(draftsMap)) {
          if (newDrafts[id] !== true) {
            newDrafts[id] = hasDraft;
          }
        }
        return newDrafts;
      });
    };

    const hasValidPosition = currentUserPosition?.trackedEntityInstance || selectedTeamMemberTEI;
    if (objectives.length > 0 && hasValidPosition) {
      checkObjectivesForTasksAndDrafts();
    }
  }, [objectives, selectedWeek, isManagerView, selectedTeamMemberTEI, currentUserPosition]);




  // Stable subtask callbacks
  const handleUpdateSubTask = useCallback((subTaskIndex, updates) => {
    setTasks(prev => {
      const newTasks = [...prev];
      const currentSubTasks = [...(newTasks[selectedTaskIndex]?.subTasks || [])];
      const updatedSubTask = { ...currentSubTasks[subTaskIndex], ...updates };
      currentSubTasks[subTaskIndex] = updatedSubTask;

      const updatedTask = { ...newTasks[selectedTaskIndex], subTasks: currentSubTasks };
      updatedTask.progressPercent = calculateTaskProgress(currentSubTasks);

      newTasks[selectedTaskIndex] = updatedTask;
      return newTasks;
    });

    // Mark as unsaved but don't trigger immediate re-render of all subtasks
    if (!isFormDisabled() && selectedObjective) {
      setHasUnsavedChanges(true);
    }
  }, [selectedTaskIndex, selectedObjective, isFormDisabled]);

  const handleRemoveSubTask = useCallback((subTaskIndex) => {
    setTasks(prev => {
      const newTasks = [...prev];
      const updatedSubTasks = (newTasks[selectedTaskIndex]?.subTasks || []).filter((_, i) => i !== subTaskIndex);
      const updatedTask = { ...newTasks[selectedTaskIndex], subTasks: updatedSubTasks };
      updatedTask.progressPercent = calculateTaskProgress(updatedSubTasks);
      newTasks[selectedTaskIndex] = updatedTask;
      return newTasks;
    });
    setHasUnsavedChanges(true);
  }, [selectedTaskIndex]);


  useEffect(() => {
    if (!selectedObjective) return;

    // Comprehensive check for ANY content
    const hasAnyContent = tasks.some(task => {
      const hasValidTaskName = task.taskName?.trim() &&
        task.taskName !== 'Unnamed Personal Performance' &&
        task.taskName !== '';
      const hasTaskDescription = task.taskDescription?.trim() &&
        task.taskDescription.trim().length > 3;
      const hasAchievements = task.achievements?.trim() &&
        task.achievements.trim().length > 5;
      const hasChallenges = hasMeaningfulChallenge(task.challenges);
      const hasActivities = task.subTasks && task.subTasks.length > 0;
      const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;

      return hasValidTaskName || hasTaskDescription || hasAchievements ||
        hasChallenges || hasActivities || hasProgress;
    });

    // Only update if value changed
    setObjectiveDrafts(prev => {
      const currentStatus = prev[selectedObjective.id] || false;
      if (currentStatus !== hasAnyContent) {
        console.log(`📝 [TOUCHED] ${selectedObjective.name}: ${hasAnyContent ? 'TOUCHED ✅' : 'NOT TOUCHED ⚠️'}`);
        return { ...prev, [selectedObjective.id]: hasAnyContent };
      }
      return prev;
    });
  }, [tasks, selectedObjective]);


  // 4. Also check existingTasks for touched status (for objectives not currently selected)
  useEffect(() => {
    if (existingTasks.length === 0) return;

    // Group existing tasks by objective ID
    const tasksByObjective = {};
    existingTasks.forEach(task => {
      if (task.objectiveId) {
        if (!tasksByObjective[task.objectiveId]) {
          tasksByObjective[task.objectiveId] = [];
        }
        tasksByObjective[task.objectiveId].push(task);
      }
    });

    // Update status for objectives that have content
    Object.entries(tasksByObjective).forEach(([objectiveId, objectiveTasks]) => {
      const hasContent = objectiveTasks.some(task =>
        task.taskName?.trim() && task.taskName !== 'Unnamed Personal Performance' ||
        task.subTasks?.length > 0 ||
        task.progressPercent > 0 ||
        (task.achievements?.trim()?.length > 0) ||
        hasMeaningfulChallenge(task.challenges)
      );

      if (hasContent) {
        setObjectiveDrafts(prev => {
          if (!prev[objectiveId]) {
            console.log(`📝 [TOUCHED from existing tasks] Objective ${objectiveId}: TOUCHED ✅`);
            return { ...prev, [objectiveId]: true };
          }
          return prev;
        });
      }
    });
  }, [existingTasks]);

  useEffect(() => {
    if (!selectedObjective) return;
    if (tasks.length === 0) return;

    // Force check for content right after tasks load
    const hasAnyContent = tasks.some(task => {
      const hasValidTaskName = task.taskName?.trim() &&
        task.taskName !== 'Unnamed Personal Performance' &&
        task.taskName !== '';
      const hasTaskDescription = task.taskDescription?.trim() && task.taskDescription.trim().length > 3;
      const hasAchievements = task.achievements?.trim() && task.achievements.trim().length > 5;
      const hasChallenges = hasMeaningfulChallenge(task.challenges);
      const hasActivities = task.subTasks && task.subTasks.length > 0;
      const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;

      return hasValidTaskName || hasTaskDescription || hasAchievements ||
        hasChallenges || hasActivities || hasProgress;
    });

    console.log(`🔍 [INITIAL LOAD] ${selectedObjective.name}: hasContent = ${hasAnyContent}`);

    setObjectiveDrafts(prev => {
      const currentStatus = prev[selectedObjective.id] || false;
      if (currentStatus !== hasAnyContent) {
        return {
          ...prev,
          [selectedObjective.id]: hasAnyContent
        };
      }
      return prev;
    });
  }, [selectedObjective, tasks]);



  // Also stabilize task-level update if used elsewhere
  const updateCurrentTask = useCallback((updates) => {
    // ✅ Log when challenges are being updated
    if (updates.challenges !== undefined) {
      console.log('📝 [CHALLENGES] Updating challenges:', {
        oldValue: currentTask?.challenges,
        newValue: updates.challenges,
        hasContent: hasMeaningfulChallenge(updates.challenges)
      });
    }

    setTasks(prev => {
      const newTasks = [...prev];
      const updatedTask = { ...newTasks[selectedTaskIndex], ...updates };
      if (updates.subTasks !== undefined) {
        updatedTask.progressPercent = calculateTaskProgress(updatedTask.subTasks);
      }
      newTasks[selectedTaskIndex] = updatedTask;
      return newTasks;
    });

    if (!isFormDisabled()) {
      setHasUnsavedChanges(true);
    }
  }, [selectedTaskIndex, isFormDisabled, currentTask?.challenges]);


  const disabledStyle = {
    backgroundColor: '#f8f9fa',
    cursor: 'not-allowed',
    opacity: 0.7
  };

  const renderFormField = (label, children, required = true) => (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontWeight: '400', marginBottom: '2px' }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      {children}
    </div>
  );


  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
      }}>
        <CircularLoader small />
        <span style={{ marginLeft: '12px' }}>Loading objectives...</span>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        className="weekly-tracker-container"
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '24px',
          backgroundColor: '#ffffffff',
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          minHeight: '100vh',
        }}
      >
        <h4
          className="section-title"
          style={{
            textAlign: 'center',
            color: '#297934ff',
            marginBottom: '20px',
            paddingBottom: '10px',
            borderBottom: '2px solid #dfe3f7ff',
            fontSize: window.innerWidth < 768 ? '1rem' : '1.1rem',
          }}
        >
          {isManagerView ? '👥 Team Personal Performance Notes' : 'Personal Performance Notes Tracking (PPN)'}
        </h4>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={() => navigate('/ppn')}
            style={{
              padding: '8px 16px',
              backgroundColor: !isManagerView ? '#1976d2' : '#e0e0e0',
              color: !isManagerView ? 'white' : '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <UserRoundIcon size={16} /> My PPN View
          </button>
          <button
            onClick={() => navigate('/ppn/manager')}
            style={{
              padding: '8px 16px',
              backgroundColor: isManagerView ? '#1976d2' : '#e0e0e0',
              color: isManagerView ? 'white' : '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <UsersRoundIcon size={16} /> Team Member PPN View
          </button>
        </div>

        {/* Team Member Selector - Only for Manager View */}
        {isManagerView && teamMembers.length > 0 && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <label style={{ fontWeight: '500', display: 'block', marginBottom: '8px' }}>
              Select Team Member:
            </label>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              {teamMembers.map(member => (
                <button
                  key={member.positionTei}
                  onClick={() => {
                    setSelectedTeamMemberTEI(member.positionTei);
                    // Reset objective selection when changing team member
                    setSelectedObjective(null);
                    setTasks([{
                      id: Date.now().toString(),
                      taskName: "",
                      taskDescription: "",
                      progressPercent: 0,
                      status: "",
                      achievements: "",
                      challenges: "",
                      nextSteps: "",
                      kpiId: "",
                      kpiName: "",
                      isNew: true
                    }]);
                    setCurrentStep('SELECT_OBJECTIVE');
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    backgroundColor: selectedTeamMemberTEI === member.positionTei ? '#2c6693' : '#ffffff',
                    color: selectedTeamMemberTEI === member.positionTei ? 'white' : '#333',
                    border: `2px solid ${selectedTeamMemberTEI === member.positionTei ? '#2c6693' : '#ddd'}`,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s'
                  }}
                >
                  {member.firstName} {member.surname}
                  <span style={{
                    fontSize: '0.7rem',
                    opacity: 0.7,
                    marginLeft: '6px'
                  }}>
                    ({member.positionTitle})
                  </span>
                </button>
              ))}
            </div>
            {selectedTeamMemberTEI && (
              <div style={{
                marginTop: '12px',
                padding: '8px 12px',
                backgroundColor: '#e8f4fd',
                borderRadius: '4px',
                fontSize: '0.85rem',
                color: '#2c6693'
              }}>
                ✓ Viewing objectives for selected team member
              </div>
            )}
          </div>
        )}

        {/* Notification */}
        {notification && (
          <div
            className="notification-toast"
            style={{
              position: 'fixed',
              bottom: '24px',
              left: '50%',
              transform: 'translateX(-50%)',
              right: 'auto',
              zIndex: 10000,
              width: 'auto',
              maxWidth: window.innerWidth < 768 ? 'calc(100vw - 32px)' : '480px',
              minWidth: window.innerWidth < 480 ? '260px' : '320px',
              pointerEvents: 'none',   // let clicks pass through the wrapper...
            }}
          >
            <div style={{ pointerEvents: 'auto' }}>   {/* ...but the alert itself stays clickable */}
              <AlertBar
                {...(notification.type === 'success'
                  ? { success: true }
                  : { warning: true })}
                onHidden={() => setNotification(null)}
              >
                {notification.message}
              </AlertBar>
            </div>
          </div>
        )}

        {/* Two-column layout - responsive */}
        <div
          className="two-column-layout"
          style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr',
            gap: '24px'
          }}
        >

          {/* Step 1: Select Objective */}
          {currentStep === 'SELECT_OBJECTIVE' && (
            <div>
              <h4 style={{
                marginBottom: '16px',
                color: '#2c3e50',
                fontSize: window.innerWidth < 768 ? '0.9rem' : '1.0rem'
              }}>
                Your Objectives
              </h4>

              {/* Objective Selection Zone */}
              <ObjectiveSelectionDisplay
                selectedObjective={selectedObjective}
                onClear={handleClearObjective}
                hasTaskThisWeek={hasSubmittedTaskThisWeek}
              />

              {/* Search Box */}
              {renderFormField(
                'Search Objective',
                <div style={{
                  position: 'relative',
                  flex: 1,
                  width: '100%'
                }}>
                  <IconSearch24 style={{
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#666',
                    pointerEvents: 'none',
                    zIndex: 10,
                    width: '20px',
                    height: '20px'
                  }} />
                  <Input
                    type="text"
                    placeholder="Search objectives..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{
                      paddingLeft: '3.5rem',
                      paddingRight: '1rem',
                      fontSize: window.innerWidth < 768 ? '14px' : '16px',
                    }}
                  />
                </div>,
                false
              )}

              {/* Objectives List - Mobile optimized */}
              <div
                style={{
                  maxHeight: window.innerWidth < 768 ? '400px' : '500px',
                  overflowY: 'auto',
                  padding: window.innerWidth < 768 ? '8px' : '12px',
                  border: '1px solid #aeb8f1ff',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  backgroundColor: '#e9f7efff',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {filteredObjectives.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: '#e79506ff',
                      fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem',
                    }}
                  >
                    {objectives.length === 0
                      ? 'No objectives found. Create an objective first.'
                      : 'No objectives match your search'}
                  </div>
                ) : (
                  filteredObjectives.map((objective) => (
                    <DraggableObjectiveCard
                      key={objective.id}
                      objective={objective}
                      isSelected={selectedObjective?.id === objective.id}
                      onSelect={(obj) => {
                        handleObjectiveSelect(obj);
                        setCurrentStep('SELECT_KPI');
                      }}
                      hasSubmittedTaskThisWeek={objectiveTasks[objective.id] || false}
                      hasDraftThisWeek={objectiveDrafts[objective.id] || false}
                      isWeekLocked={isSpecificWeekLocked(selectedWeek)}
                      currentTasks={selectedObjective?.id === objective.id
                         ? tasks
                         : existingTasks.filter(t => t.objectiveId === objective.id)}
                      selectedWeek={selectedWeek}
                    />
                  ))
                )}
              </div>
            </div>
          )}

           {/* Back button row - Only show when NOT on the main objective selection screen */}
          {currentStep !== 'SELECT_OBJECTIVE' && (
            <div style={{ marginBottom: '12px' }}>
              <button
                onClick={handleBackToObjective}
                style={{
                  marginBottom: '16px',
                  backgroundColor: '#053e70ff',
                  color: '#f6f7f9ff',
                  borderRadius: '8px',
                  padding: window.innerWidth < 768 ? '10px 16px' : '8px 16px',
                  border: 'none',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  cursor: 'pointer',
                  width: window.innerWidth < 768 ? '100%' : 'auto',
                  fontSize: window.innerWidth < 768 ? '14px' : '16px',
                }}
              >
                ← Back to Personal Performance Page
              </button>
            </div>
          )}
          {/* Step 2: Select KPI - Modern card UI */}
          {currentStep === 'SELECT_KPI' && selectedObjective && (
            <div>
              {/* Compact objective chip */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px',
                  padding: '12px 20px',
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #a5d6a7',
                  borderRadius: '10px',
                  fontSize: '1.25rem',
                  color: '#1b5e20',
                  fontWeight: 600,
                }}
              >
                <span style={{ opacity: 0.7 }}>Objective Name:</span>
                <span style={{ fontWeight: 600 }}>{selectedObjective.name}</span>
              </div>

              {/* Compact amber notice — replaces the big banner + finger pointer */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 16px',
                  marginBottom: '16px',
                  backgroundColor: '#fff8e1',
                  border: '1px solid #ffd54f',
                  borderLeft: '4px solid #ff9800',
                  borderRadius: '8px',
                  color: '#7a4a00',
                  fontSize: '0.85rem',
                }}
              >
                <span style={{ fontSize: '1.1rem', lineHeight: 1 }}> <IconArrowDown24 /></span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'baseline' }}>
                  <strong style={{ color: '#e65100' }}>Select a KPI</strong>
                  <span style={{ opacity: 0.85 }}>to link to this Personal Performance Note</span>
                </div>
              </div>

              {/* KPI cards grid */}
              {kpis.length > 0 ? (
                <div
                  style={{
                    maxHeight: window.innerWidth < 768 ? '420px' : '360px',
                    overflowY: 'auto',
                    backgroundColor: 'transparent',
                    borderRadius: '10px',
                    display: 'grid',
                    gridTemplateColumns:
                      window.innerWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: window.innerWidth < 768 ? '12px' : '14px',
                    padding: window.innerWidth < 768 ? '4px' : '6px',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {kpis.map((kpi) => {
                    const isSelected = selectedKPI?.eventId === kpi.eventId;
                    return (
                      <button
                        key={kpi.id}
                        type="button"
                        onClick={() => {
                          setSelectedKPI(kpi);
                          updateCurrentTask({
                            kpiId: kpi.eventId,
                            kpiName: kpi.name,
                          });
                          setCurrentStep('FILL_TASK');
                        }}
                        className="kpi-select-card"
                        style={{
                          textAlign: 'left',
                          cursor: 'pointer',
                          background: '#ffffff',
                          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e4ea',
                          borderRadius: '12px',
                          padding: '16px 16px 14px',
                          boxShadow: isSelected
                            ? '0 6px 18px rgba(25,118,210,0.22)'
                            : '0 1px 3px rgba(0,0,0,0.06)',
                          transition:
                            'transform 0.15s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          position: 'relative',
                          overflow: 'hidden',
                          fontFamily: 'inherit',
                          color: 'inherit',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow =
                            '0 10px 24px rgba(25,118,210,0.18)';
                          e.currentTarget.style.borderColor = '#1976d2';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = isSelected
                            ? '0 6px 18px rgba(25,118,210,0.22)'
                            : '0 1px 3px rgba(0,0,0,0.06)';
                          e.currentTarget.style.borderColor = isSelected
                            ? '#1976d2'
                            : '#e0e4ea';
                        }}
                      >
                        {/* Top row: icon + name */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: '10px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {/* Badge icon */}
                            <div
                              style={{
                                flexShrink: 0,
                                width: '34px',
                                height: '34px',
                                borderRadius: '8px',
                                background:
                                  'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.95rem',
                                fontWeight: 700,
                                boxShadow: '0 2px 6px rgba(25,118,210,0.28)',
                              }}
                            >
                              KPI
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  fontSize: '0.95rem',
                                  fontWeight: 600,
                                  color: '#1a202c',
                                  lineHeight: 1.25,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {kpi.name}
                              </div>
                            </div>
                          </div>

                          {/* Arrow affordance */}
                          <span
                            style={{
                              flexShrink: 0,
                              color: '#90a4ae',
                              fontSize: '1.1rem',
                              lineHeight: 1,
                              transition: 'transform 0.15s ease, color 0.15s ease',
                            }}
                            className="kpi-arrow"
                          >
                            →
                          </span>
                        </div>

                        {/* Description */}
                        {kpi.description && (
                          <div
                            style={{
                              fontSize: '0.8rem',
                              color: '#64748b',
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {kpi.description}
                          </div>
                        )}

                        {/* Footer: measure + select hint */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginTop: '4px',
                            paddingTop: '8px',
                            borderTop: '1px dashed #eef1f5',
                            fontSize: '0.72rem',
                            color: '#94a3b8',
                          }}
                        >
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '70%',
                            }}
                          >
                            Measure: {kpi.target || 'N/A'}
                          </span>
                          <span
                            className="kpi-select-hint"
                            style={{
                              color: '#1976d2',
                              fontWeight: 600,
                              opacity: 0,
                              transition: 'opacity 0.15s ease',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Select →
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '10px',
                    border: '1px dashed #cbd5e1',
                    color: '#64748b',
                  }}
                >
                  <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📋</div>
                  <p style={{ margin: 0, fontWeight: 600, color: '#334155' }}>
                    No KPIs found for this objective
                  </p>
                  <p style={{ fontSize: '0.85rem', marginTop: '6px' }}>
                    Please create KPIs first in the KPI Management section.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Fill Task Form - Modern Styled */}
          {currentStep === 'FILL_TASK' && selectedObjective && selectedKPI && (
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              marginTop: '8px'
            }}>

              {/* Header Banner */}
              <div style={{
                background: '#057916',
                padding: '16px 20px',
                color: 'white'
              }}>
                <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                  📝 Progress notes
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '0.8rem', opacity: 0.9 }}>
                  <div><strong>Objective:</strong> {selectedObjective.name?.substring(0, 50)}</div>
                  <div><strong>KPI:</strong> {selectedKPI.name?.substring(0, 40)}</div>
                </div>

                {/* Read-only KPI context — target + live completion across all closed PPNs for this KPI */}
                <div style={{
                  marginTop: '10px',
                  paddingTop: '10px',
                  borderTop: '1px solid rgba(255,255,255,0.2)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '16px',
                  fontSize: '0.75rem',
                }}>
                  {selectedKPI.target && selectedKPI.target !== 'N/A' && (
                    <div>
                      <span style={{ opacity: 0.75 }}>KPI Target:</span>{' '}
                      <strong>{selectedKPI.target}</strong>
                    </div>
                  )}
                  <div>
                    <span style={{ opacity: 0.75 }}>PPNs under this KPI:</span>{' '}
                    <strong>{existingTasks.filter(t => t.kpiId === selectedKPI.eventId).length}</strong>
                  </div>
                  {(() => {
                    const sealedForKpi = existingTasks.filter(
                      t => t.kpiId === selectedKPI.eventId && t.sealed && (t.progressPercent ?? 0) > 0
                    );
                    if (sealedForKpi.length === 0) {
                      return (
                        <div style={{ opacity: 0.75, fontStyle: 'italic' }}>
                          No closed PPNs yet — this KPI will not count towards appraisal until you close one.
                        </div>
                      );
                    }
                    const avg = Math.round(
                      sealedForKpi.reduce((s, t) => s + (t.progressPercent ?? 0), 0) / sealedForKpi.length
                    );
                    return (
                      <div>
                        <span style={{ opacity: 0.75 }}>KPI completion (closed PPNs):</span>{' '}
                        <strong>{avg}%</strong>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Week selector — scoped to the FILL_TASK step, so changing week
                keeps the KPI selected and just re-seeds the task list. */}
              <div style={{
                padding: '12px 20px 0 20px',
                background: '#ffffff',
                borderBottom: '1px solid #e9ecef',
              }}>
<WeekChipSelector
                  weeks={allWeeks}
                  selectedWeek={selectedWeek}
                  onWeekSelect={(week) => {
                     if (week === selectedWeek) return;
                     lastSeedKeyRef.current = '';
                     setWeekEnding(week);
                     setSelectedTaskIndex(0);
                     setHasUnsavedChanges(false);
                     if (selectedObjective) {
                       loadExistingTasksForWeek(week);
                     }
                   }}
                  existingTasks={existingTasks}
                />
              </div>

              {/* Content Area */}
              <div style={{ padding: '20px' }}>              

                {/* ⬇ Task switcher — full-width, own band, above the stats row */}
                <TaskSwitcher
                  tasks={tasks}
                  selectedTaskIndex={selectedTaskIndex}
                  onSelect={(idx) => {
                    if (idx === selectedTaskIndex) return;
                    setSelectedTaskIndex(idx);
                    setHasUnsavedChanges(false);
                    const newTaskKey = tasks[idx]?.eventId || tasks[idx]?.id || '';
                    challengeBaselineByTaskRef.current[newTaskKey] = tasks[idx]?.challenges || '';
                  }}
                  onAdd={() => {
                    const newTask = createEmptyTask(selectedKPI.eventId, selectedKPI.name);
                    newTask.selectedWeek = selectedWeek;
                    setTasks((prev) => [...prev, newTask]);
                    setSelectedTaskIndex(tasks.length);   // select the newly added one
                    setHasUnsavedChanges(true);
                  }}
                  onRemove={handleRemoveCurrentTask}
                  disabled={isFormDisabled()}
                />

                {/* Share with manager */}
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '10px 14px',
                    background: currentTask.shareWithManager ? '#e8f5e9' : '#f8fafc',
                    border: `1px solid ${currentTask.shareWithManager ? '#a5d6a7' : '#e0e4ea'}`,
                    borderLeft: `4px solid ${currentTask.shareWithManager ? '#43a047' : '#94a3b8'}`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <input
                    id={`share-${currentTask?.eventId || currentTask?.id || 'new'}`}
                    type="checkbox"
                    checked={currentTask.shareWithManager === true}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      updateCurrentTask({ shareWithManager: checked });
                      // Save immediately so the change is persisted on the next blur even if user
                      // doesn't touch another field
                    }}
                    onBlur={saveOnBlur}
                    disabled={isFormDisabled()}
                    style={{
                      width: '18px',
                      height: '18px',
                      marginTop: '2px',
                      cursor: isFormDisabled() ? 'not-allowed' : 'pointer',
                      accentColor: '#43a047',
                      flexShrink: 0,
                    }}
                  />
                  <label
                    htmlFor={`share-${currentTask?.eventId || currentTask?.id || 'new'}`}
                    style={{
                      cursor: isFormDisabled() ? 'not-allowed' : 'pointer',
                      flex: 1,
                      userSelect: 'none',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: currentTask.shareWithManager ? '#1b5e20' : '#334155',
                      }}
                    >
                      📤 Share this PPN with my manager
                    </div>
                    <div
                      style={{
                        fontSize: '0.78rem',
                        color: currentTask.shareWithManager ? '#2e7d32' : '#64748b',
                        marginTop: '2px',
                      }}
                    >
                      {currentTask.shareWithManager
                        ? 'Your manager can view this PPN in their Team Member PPN view.'
                        : 'This PPN is private. Your manager will not see it.'}
                    </div>
                  </label>
                </div>

                {/* ── Row 2: Progress | Sync | Close — responsive grid ── */}
                {(() => {
                  const isMobile = window.innerWidth < 600;
                  const isTablet = window.innerWidth >= 600 && window.innerWidth < 900;

                  const gridTemplateColumns = isMobile
                    ? '1fr'
                    : isTablet
                      ? 'minmax(200px, 1fr) 90px'
                      : 'minmax(240px, 1fr) 90px 200px';

                  const progressGridColumn = 'auto';
                  const sealGridColumn = isTablet ? '1 / -1' : 'auto';

                  const smallLabel = {
                    fontWeight: 600,
                    marginBottom: '4px',
                    display: 'block',
                    fontSize: '0.72rem',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  };

                  const progress = currentTask?.progressPercent ?? 0;
                  const progressColor = getProgressColor(progress);
                  const tier = getProgressTier(currentTask?.taskName, progress);

                  return (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns,
                      gap: '12px',
                      alignItems: 'stretch',
                      marginBottom: '20px',
                      padding: '12px 16px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                    }}>
                      {/* PROGRESS */}
                      <div style={{ gridColumn: progressGridColumn, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          marginBottom: '4px',
                        }}>
                          <label style={{ ...smallLabel, marginBottom: 0 }}>Progress</label>
                          <span style={{ fontWeight: 800, fontSize: '0.85rem', color: progressColor }}>
                            {progress}%
                          </span>
                        </div>

                        {/* Dynamic tier label */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '6px',
                        }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: tier.color,
                            background: `${tier.color}18`,
                            border: `1px solid ${tier.color}55`,
                            whiteSpace: 'nowrap',
                          }}>
                            {tier.label}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '0.72rem',
                          color: '#64748b',
                          marginBottom: '6px',
                          lineHeight: 1.35,
                        }}>
                          {tier.caption}
                        </div>

                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={progress}
                          disabled={isFormDisabled()}
                          onChange={(e) => {
                            const newProgress = Number(e.target.value);
                            const updates = { progressPercent: newProgress };

                            // Auto-derive the persisted status from the progress value.
                            if (newProgress >= 100) {
                              updates.status = 'completed';
                            } else if (newProgress > 0) {
                              updates.status = 'inprogress';
                            } else {
                              updates.status = 'pending';
                            }

                            updateCurrentTask(updates);
                          }}
                          onBlur={saveOnBlur}
                          style={{
                            width: '100%',
                            height: 8,
                            borderRadius: 4,
                            background: `linear-gradient(
                              to right,
                              ${progressColor} 0%,
                              ${progressColor} ${progress}%,
                              #e9ecef ${progress}%,
                              #e9ecef 100%
                            )`,
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            appearance: 'none',
                            outline: 'none',
                            cursor: isFormDisabled() ? 'not-allowed' : 'pointer',
                          }}
                        />
                      </div>

                      {/* SYNC */}
                      <div style={{ textAlign: 'center', alignSelf: 'center' }}>
                        <label style={{ ...smallLabel, textAlign: 'center' }}>Sync</label>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                        }}>
                          {isAutoSaving ? (
                            <CircularLoader small style={{ width: '16px', height: '16px' }} />
                          ) : hasUnsavedChanges ? (
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ffc107' }} />
                          ) : lastAutoSaved ? (
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#28a745' }} />
                          ) : (
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#6c757d' }} />
                          )}
                          <span style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                            {isAutoSaving ? 'Saving' : hasUnsavedChanges ? 'Unsaved' : lastAutoSaved ? 'Saved' : 'Ready'}
                          </span>
                        </div>
                      </div>

                      {/* CLOSE (was SEAL) */}
                      <div style={{ gridColumn: sealGridColumn, alignSelf: 'center' }}>
                        <label style={smallLabel}>Close</label>
                        {currentTask?.taskName?.trim() ? (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.72rem',
                            background: currentTask?.sealed ? '#e8f5e9' : '#fff8e1',
                            border: `1px solid ${currentTask?.sealed ? '#a5d6a7' : '#ffd54f'}`,
                            borderRadius: '6px',
                            padding: '4px 8px',
                          }}>
                            <span style={{
                              flex: 1,
                              color: currentTask?.sealed ? '#1b5e20' : '#e65100',
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}>
                              {currentTask?.sealed
                                ? `Closed at ${currentTask?.progressPercent ?? 0}%`
                                : (currentTask?.progressPercent ?? 0) >= 100
                                  ? 'Not closed'
                                  : `${currentTask?.progressPercent ?? 0}% — close to count`}
                            </span>
                            <button
                              type="button"
                              disabled={!currentTask?.sealed && isFormDisabled()}
                              onClick={async () => {
                                if (currentTask?.sealed) {
                                  updateCurrentTask({ sealed: false });
                                  setTimeout(() => saveOnBlur(), 100);
                                  showNotification('PPN opened.', 'success');
                                  return;
                                }
                                const p = currentTask?.progressPercent ?? 0;
                                if (p >= 100) {
                                  updateCurrentTask({ sealed: true });
                                  setTimeout(() => saveOnBlur(), 100);
                                  showNotification('PPN closed — it will count in your appraisal.', 'success');
                                  return;
                                }
                                setSealReasonChoice(null);
                                setSealReasonModalOpen(true);
                              }}
                              style={{
                                padding: '3px 10px',
                                borderRadius: '4px',
                                border: currentTask?.sealed ? '1px solid #c62828' : 'none',
                                background: currentTask?.sealed ? '#fff' : '#43a047',
                                color: currentTask?.sealed ? '#c62828' : '#fff',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                cursor: isFormDisabled() ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {currentTask?.sealed ? 'Open' : '🔒 Close'}
                            </button>
                          </div>
                        ) : (
                          <div style={{
                            fontSize: '0.72rem',
                            color: '#94a3b8',
                            fontStyle: 'italic',
                            padding: '4px 8px',
                          }}>
                            Add PPN name first
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ═══════════════════════════════════════════════════════════
                 STRETCH CARD — visible only at ≥100% progress with a name
                 ═══════════════════════════════════════════════════════════ */}
                {(currentTask?.progressPercent ?? 0) >= 100 && currentTask?.taskName?.trim() && (
                  <div
                    style={{
                      marginBottom: '20px',
                      border: '1px solid #c7d2fe',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      background: currentTask?.stretchTier ? '#f0f4ff' : '#f8fafc',
                    }}
                  >
                    {/* ── Header ── */}
                    <div
                      style={{
                        padding: '10px 14px',
                        background: currentTask?.stretchTier ? '#e0e7ff' : '#e2e8f0',
                        borderBottom: `1px solid ${currentTask?.stretchTier ? '#a5b4fc' : '#cbd5e1'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <span style={{ fontSize: '1.05rem' }}>📈</span>
                      <span
                        style={{
                          fontWeight: 700,
                          color: currentTask?.stretchTier ? '#3730a3' : '#475569',
                          fontSize: '0.82rem',
                        }}
                      >
                        Achievement Tier
                      </span>
                      <span
                        style={{
                          fontSize: '0.7rem',
                          color: currentTask?.stretchTier ? '#4338ca' : '#64748b',
                          marginLeft: 'auto',
                        }}
                      >
                        Reviewed by your manager at appraisal
                      </span>
                    </div>

                    {/* ── Body ── */}
                    <div style={{ padding: '12px 14px' }}>
                      {/* Two mutually-exclusive tier cards */}
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {[
                        STRETCH_TIER_CONFIG[STRETCH_TIERS.TARGET_EXCEEDED],
                        STRETCH_TIER_CONFIG[STRETCH_TIERS.EXCEPTIONAL],
                      ].map((cfg) => {
                        const isActive = currentTask?.stretchTier === cfg.value;
                        return (
                          <button
                            type="button"
                            key={cfg.value}
                            disabled={isFormDisabled()}
                            onClick={() => {
                              const nextTier = isActive ? STRETCH_TIERS.NONE : cfg.value;
                              updateCurrentTask({
                                stretchTier: nextTier,
                                ...(nextTier === STRETCH_TIERS.NONE
                                  ? { stretchCategories: [], stretchEvidence: '' }
                                  : {}),
                              });
                              setTimeout(() => saveOnBlur(), 100);
                            }}
                            style={{
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '10px',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              border: `1.5px solid ${isActive ? cfg.border : '#cbd5e1'}`,
                              background: isActive ? cfg.bg : '#fff',
                              cursor: isFormDisabled() ? 'not-allowed' : 'pointer',
                              transition: 'all 0.15s',
                            }}
                          >
                            {/* Radio-style indicator dot */}
                            <span
                              aria-hidden
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                border: `2px solid ${isActive ? cfg.color : '#94a3b8'}`,
                                background: isActive ? cfg.color : 'transparent',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                marginTop: 2,
                              }}
                            >
                              {isActive && (
                                <span
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: '#fff',
                                  }}
                                />
                              )}
                            </span>

                            {/* Text block — label + description, no rating hint */}
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: '0.85rem',
                                  color: '#1e293b',
                                }}
                              >
                                {cfg.label}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
                                {cfg.description}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                      </div>

                      {/* Categories + evidence — visible only when a tier is chosen */}
                      {currentTask?.stretchTier && (
                        <>
                          {/* ── Categories ── */}
                          <div style={{ marginTop: 12 }}>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: '#334155',
                                marginBottom: 6,
                              }}
                            >
                              What kind of achievement?{' '}
                              <span style={{ color: '#e74c3c' }}>*</span>{' '}
                              (choose at least one)
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {STRETCH_CATEGORY_OPTIONS.map((opt) => {
                                const active = (currentTask?.stretchCategories || []).includes(opt.value);
                                return (
                                  <button
                                    type="button"
                                    key={opt.value}
                                    disabled={isFormDisabled()}
                                    onClick={() => {
                                      const current = currentTask?.stretchCategories || [];
                                      const next = active
                                        ? current.filter((v) => v !== opt.value)
                                        : [...current, opt.value];
                                      updateCurrentTask({ stretchCategories: next });
                                      setTimeout(() => saveOnBlur(), 100);
                                    }}
                                    style={{
                                      padding: '5px 10px',
                                      borderRadius: '14px',
                                      border: `1.5px solid ${active ? '#4f46e5' : '#cbd5e1'}`,
                                      background: active ? '#4f46e5' : '#fff',
                                      color: active ? '#fff' : '#334155',
                                      fontSize: '0.72rem',
                                      fontWeight: active ? 700 : 500,
                                      cursor: isFormDisabled() ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* ── Evidence ── */}
                          <div style={{ marginTop: 12 }}>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: '#334155',
                                marginBottom: 4,
                              }}
                            >
                              Evidence <span style={{ color: '#e74c3c' }}>*</span>{' '}
                              (min 30 characters)
                            </div>
                            <TextArea
                              value={currentTask?.stretchEvidence || ''}
                              onChange={({ value }) => updateCurrentTask({ stretchEvidence: value })}
                              onBlur={saveOnBlur}
                              disabled={isFormDisabled()}
                              rows={3}
                              placeholder="What did you do beyond the target? Be specific — this will be reviewed by your manager."
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #c7d2fe',
                                padding: '10px',
                                fontSize: '0.85rem',
                                background: '#fff',
                              }}
                            />
                            <div
                              style={{
                                fontSize: '0.7rem',
                                marginTop: 4,
                                color:
                                  (currentTask?.stretchEvidence || '').trim().length >= 30
                                    ? '#16a34a'
                                    : '#94a3b8',
                              }}
                            >
                              {(currentTask?.stretchEvidence || '').trim().length} / 30 characters
                            </div>
                          </div>

                          {/* ── Informational note ── */}
                          <div
                            style={{
                              marginTop: 10,
                              padding: '8px 10px',
                              background: '#fef3c7',
                              borderRadius: 6,
                              fontSize: '0.72rem',
                              color: '#92400e',
                            }}
                          >
                            ℹ️ This achievement only counts towards your appraisal when this PPN
                            is <strong>closed</strong>.
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════════
                CLOSE-REASON MODAL — appears when closing below 100%
                 ═══════════════════════════════════════════════════════════ */}
                {sealReasonModalOpen && (
                  <Modal small onClose={() => { setSealReasonModalOpen(false); setSealReasonChoice(null); }}>
                    <ModalTitle>Close this PPN at {currentTask?.progressPercent ?? 0}%?</ModalTitle>
                    <ModalContent>
                      <div style={{ padding: '8px 4px' }}>
                        <p style={{ marginTop: 0, fontSize: '0.85rem', color: '#334155' }}>
                          This PPN is not at 100%. Closing it will count your current{' '}
                          <strong>{currentTask?.progressPercent ?? 0}%</strong> towards your appraisal.
                          Please tell us why it wasn't completed.
                        </p>
                        <div style={{ display: 'grid', gap: '6px', marginTop: 10 }}>
                          {SEAL_REASON_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setSealReasonChoice(opt.value)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '8px 12px',
                                borderRadius: 8,
                                border: `1.5px solid ${sealReasonChoice === opt.value ? '#1976d2' : '#e2e8f0'}`,
                                background: sealReasonChoice === opt.value ? '#e8f4fd' : '#fff',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: sealReasonChoice === opt.value ? 700 : 500,
                                color: '#1e293b',
                                textAlign: 'left',
                              }}
                            >
                              <span>{opt.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </ModalContent>
                    <ModalActions>
                      <button
                        style={{
                          backgroundColor: '#f12f0c',
                          border: '1px solid #ddd',
                          borderRadius: '20px',
                          padding: '4px 8px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          color: '#fbe6e6',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginRight: '5px',
                        }}
                        onClick={() => { setSealReasonModalOpen(false); setSealReasonChoice(null); }}
                      >
                        Cancel
                      </button>
                      <button
                        style={{
                          padding: '6px 14px',
                          borderRadius: '20px',
                          border: '1px solid #ddd',
                          color: '#e3fcda',
                          fontSize: '0.75rem',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: '#17990b',
                        }}
                        disabled={!sealReasonChoice}
                        onClick={() => {
                          updateCurrentTask({ sealed: true });
                          setTimeout(() => saveOnBlur(), 100);
                          showNotification(
                            `PPN closed at ${currentTask?.progressPercent ?? 0}% (${SEAL_REASON_OPTIONS.find((o) => o.value === sealReasonChoice)?.label}). It will count in your appraisal.`,
                            'success'
                          );
                          setSealReasonModalOpen(false);
                          setSealReasonChoice(null);
                        }}
                      >
                        🔒 Close PPN
                      </button>
                    </ModalActions>
                  </Modal>
                )}

                {/* Row 1: Personal Performance Name — full width */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    fontWeight: 600,
                    marginBottom: '4px',
                    display: 'block',
                    fontSize: '0.75rem',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Personal Performance Name <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <Input
                    value={currentTask.taskName}
                    onChange={({ value }) => {
                      if (!isPastWeek(selectedWeek) || currentTask.taskName) {
                        updateCurrentTask({ taskName: value });
                      }
                    }}
                    onBlur={saveOnBlur}
                    placeholder={
                      isPastWeek(selectedWeek) && !currentTask.taskName
                        ? 'Cannot add new Personal Performances in past weeks'
                        : 'e.g., Complete Q4 Financial Report'
                    }
                    disabled={isFormDisabled() || (isPastWeek(selectedWeek) && !currentTask.taskName)}
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #e0e0e0',
                      padding: '8px 12px',
                      fontSize: '0.85rem',
                      width: '100%',
                    }}
                  />
                </div>

                {/* Personal Performance Description - Modern TextArea */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    fontWeight: 600,
                    marginBottom: '8px',
                    display: 'block',
                    fontSize: '0.85rem',
                    color: '#2c3e50'
                  }}>
                    Personal Performance Description
                  </label>
                  <TextArea
                    value={currentTask.taskDescription}
                    onChange={({ value }) => updateCurrentTask({ taskDescription: value })}
                    onBlur={saveOnBlur}
                    placeholder="Describe what you plan to do in detail..."
                    rows={3}
                    disabled={isFormDisabled()}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                      padding: '12px',
                      fontSize: '0.9rem',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {/* Achievements Card */}
                <div style={{
                  marginBottom: '20px',
                  border: '1px solid #e8f5e9',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  backgroundColor: '#f1f8e9'
                }}>
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#e8f5e9',
                    borderBottom: '1px solid #c8e6c9',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>🏆</span>
                    <span style={{ fontWeight: 600, color: '#2e7d32' }}>Achievements / Milestones</span>
                  </div>
                  <div style={{ padding: '16px' }}>
                    <TextArea
                      value={currentTask.achievements}
                      onChange={({ value }) => updateCurrentTask({ achievements: value })}
                      onBlur={saveOnBlur}
                      placeholder="What have you accomplished so far? Be specific..."
                      rows={3}
                      disabled={isFormDisabled()}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #c8e6c9',
                        padding: '12px',
                        fontSize: '0.9rem',
                        fontFamily: 'inherit',
                        backgroundColor: 'white'
                      }}
                    />
                  </div>
                </div>

                {/* Challenges Card */}

                <div style={{
                  marginBottom: '10px',
                  border: '1px solid #fff3e0',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  backgroundColor: '#fff8e1'
                }}>
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#fff3e0',
                    borderBottom: '1px solid #ffe0b2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                    <span style={{ fontWeight: 600, color: '#e65100' }}>Challenges / Blockers</span>
                  </div>
                  <div style={{ padding: '16px' }}>
                    <TextArea
                      value={currentTask.challenges}
                      onChange={({ value }) => updateCurrentTask({ challenges: value })}
                      onBlur={saveOnBlur}
                      placeholder="What obstacles are you facing? What support do you need?"
                      rows={3}
                      disabled={isFormDisabled()}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #ffe0b2',
                        padding: '12px',
                        fontSize: '0.9rem',
                        fontFamily: 'inherit',
                        backgroundColor: 'white'
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '2px', marginBottom: '20px' }}>
                  {hasMeaningfulChallenge(currentTask?.challenges) && (
                    <button
                      hidden={!userHasSupervisor}
                      title={
                        userHasSupervisor
                          ? 'Send a notification to your supervisor'
                          : 'No supervisor configured for your position'
                      }
                      onClick={async () => {
                        if (!userHasSupervisor) return;
                        console.log('📧 [MANUAL] Sending challenge notification...');
                        try {
                          const result = await sendChallengeNotification(
                            currentTask,
                            selectedObjective,
                            currentTask.challenges
                          );
                          if (result.success) {
                            showNotification(`Challenge notification sent to ${result.recipientName || 'manager'}`, 'success');
                            const taskKey = currentTask?.eventId || currentTask?.id || '';
                            challengeNotificationSentRef.current = true;
                            challengeNotifiedByTaskRef.current[taskKey] = currentTask.challenges;
                            challengeBaselineByTaskRef.current[taskKey] = currentTask.challenges;
                          } else {
                            showNotification('Failed to send: ' + (result.error || 'Unknown error'), 'error');
                          }
                        } catch (error) {
                          console.error('❌ [MANUAL] Error:', error);
                          showNotification('Error sending notification', 'error');
                        }
                      }}
                      style={{
                        backgroundColor: userHasSupervisor ? '#ff9800' : '#cbd5e1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        cursor: userHasSupervisor ? 'pointer' : 'not-allowed',
                        fontSize: '0.8rem',
                      }}
                    >
                      📧 Send Challenge Notification to your supervisor
                    </button>
                  )}
                  {currentTask?.challenges?.trim().length > 0 && !hasMeaningfulChallenge(currentTask?.challenges) && (
                    <span style={{ fontSize: '0.75rem', color: '#999', fontStyle: 'italic' }}>
                      Enter a specific challenge to send a notification
                    </span>
                  )}
                </div>

                {/* Next Steps Card */}
                <div style={{
                  marginBottom: '24px',
                  border: '1px solid #e3f2fd',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  backgroundColor: '#e3f2fd'
                }}>
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#e3f2fd',
                    borderBottom: '1px solid #bbdefb',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>📋</span>
                    <span style={{ fontWeight: 600, color: '#1565c0' }}>Next Steps</span>
                  </div>
                  <div style={{ padding: '16px' }}>
                    <TextArea
                      value={currentTask.nextSteps}
                      onChange={({ value }) => updateCurrentTask({ nextSteps: value })}
                      onBlur={saveOnBlur}
                      placeholder="What are your planned next actions? What's the timeline?"
                      rows={2}
                      disabled={isFormDisabled()}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #bbdefb',
                        padding: '12px',
                        fontSize: '0.9rem',
                        fontFamily: 'inherit',
                        backgroundColor: 'white'
                      }}
                    />
                  </div>
                </div>

                {/* File Attachment */}
                {currentTask.eventId ? (
                  <FileAttachment
                    key={currentTask.eventId}
                    taskId={currentTask.eventId}
                    selectedWeek={selectedWeek}
                    objectiveId={selectedObjective?.id}
                    engine={engine}
                    onFileUploaded={(file) => console.log('File uploaded:', file)}
                    onFileRemoved={(fileId) => console.log('File removed:', fileId)}
                    isFormDisabled={isFormDisabled()}
                    showNotification={showNotification}
                    currentUser={currentUser}
                  />
                ) : (
                  <div
                    style={{
                      padding: '16px',
                      background: '#f8fafc',
                      border: '1px dashed #cbd5e1',
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: '0.85rem',
                    }}
                  >
                    📎 Save this PPN first (fill in a name and blur the field) to enable file attachments.
                  </div>
                )}

                <CommentsPanel rawComments={currentTask?.managerComments} />

                {/* Tips Banner */}
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '10px',
                  marginBottom: '24px',
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>💡 Update status to track progress</span>
                    <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>📝 Add achievements as you complete milestones</span>
                    <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>⚡ Auto-saves every 3 seconds</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '8px',
                  flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => setCurrentStep('SELECT_KPI')}
                    style={{
                      backgroundColor: '#08298b',
                      color: '#f4f4f6',
                      borderRadius: '20px',
                      padding: '10px 20px',
                      border: '1px solid #dee2e6',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      width: window.innerWidth < 768 ? '100%' : 'auto',
                      transition: 'all 0.2s'
                    }}
                  >
                    ← Back to KPIs
                  </button>
                  <button
                    onClick={handleBackToObjective}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      borderRadius: '20px',
                      padding: '10px 20px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      width: window.innerWidth < 768 ? '100%' : 'auto',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#c82333';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc3545';
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DndProvider>
  );
}

export default PersonalPerformanceNotes;
